/**
 * AI Service - Unified AI generation service using the three-layer architecture
 * 
 * Frontend → User Backend → Admin Backend → External AI Providers
 * 
 * This service handles all AI generation requests through the secure backend,
 * without exposing any API keys to the frontend.
 */

import { getModelForCapability, getSelectedModels, getModelsInFallbackOrder, getSelectedTier } from '../utils/modelHelper';
import { buildShotGenerationRules, buildShotBreakdownRules } from '../utils/shotGenerationRules';
import { resolveShotDurationSeconds, buildVideoGenerationPayload } from '../utils/videoModelDurations';
import { buildCapabilityFallbackChain } from '../utils/modelCascadeResolver';
import { requestApproval as requestFallbackApproval } from '../utils/videoFallbackApprovalBroker';
import { parseScriptScenes } from '../utils/scriptSceneParser';
import { parsePanelsFromDescription } from '../utils/panelParser';
import { ensureCinematicShotDescription } from '../utils/cinematicShotDescriptionBuilder';

// CRITICAL: User Backend URL - always use app.loopai.in
const USER_BACKEND_URL = 'https://app.loopai.in';

// Get User Backend URL - always returns the correct URL
const getUserBackendUrl = () => {
    return `${USER_BACKEND_URL}/api`;
};

// ==================== VIDEO STYLE DIRECTIVE ====================
// Feb 2026 — Every image-gen call (portraits, elements, storyboard
// panels, scene images, editor refinements) prepends a visual-style
// directive so the whole project stays visually consistent — even the
// text-to-image panels that lack character/element references.
// The style is chosen by the user on the Story step and persisted to
// `localStorage.currentProjectVideoStyle` by `App.js`.
const VIDEO_STYLE_DIRECTIVES = {
    realistic: 'VISUAL STYLE: Photorealistic. Real-photography aesthetic — believable lighting, natural skin tones, real-world textures, cinematic camera. Do NOT render as illustration, cartoon, or 3D animation.',
    cartoon_animation: 'VISUAL STYLE: 2D Cartoon Animation. Stylised hand-drawn cartoon look — bold outlines, flat/cel-shaded colour, expressive exaggerated characters, playful stylised world. Do NOT render as photorealistic, 3D, or anime.',
    pixar_3d: 'VISUAL STYLE: Pixar-style 3D Animation. High-quality 3D-rendered characters with soft subsurface skin, expressive stylised proportions, rich cinematic lighting, colourful polished 3D world (Pixar / DreamWorks feel). Do NOT render as photorealistic, 2D cartoon, or anime.',
    anime: 'VISUAL STYLE: Japanese Anime. Cel-shaded anime aesthetic — large expressive eyes, clean line-art, vibrant colour blocks, dynamic anime composition and lighting. Do NOT render as photorealistic, 3D, or Western cartoon.',
};

const getVideoStyleDirective = () => {
    try {
        const style = localStorage.getItem('currentProjectVideoStyle');
        if (!style) return '';
        return VIDEO_STYLE_DIRECTIVES[style] || '';
    } catch { return ''; }
};

// Prepend the directive to a prompt. Idempotent — if the prompt already
// begins with a "VISUAL STYLE:" block we leave it alone so callers who
// build their own style prefix don't get double-injection.
const applyVideoStyleDirective = (prompt) => {
    const directive = getVideoStyleDirective();
    if (!directive) return prompt;
    if (typeof prompt !== 'string') return prompt;
    if (/^\s*VISUAL STYLE:/i.test(prompt)) return prompt;
    return `${directive}\n\n${prompt}`;
};



// Common fetch wrapper with error handling
const apiCall = async (endpoint, options = {}, timeout = 120000) => {
    const apiUrl = getUserBackendUrl();
    const url = `${apiUrl}${endpoint}`;
    
    // Get API key and user email from sessionStorage for AI endpoints
    const apiKey = sessionStorage.getItem('apiKey');
    const userEmail = sessionStorage.getItem('userEmail');
    
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    };
    
    // Add API key header if available (needed for /ai/* and /webhook/* endpoints)
    if (apiKey) {
        defaultOptions.headers['X-API-Key'] = apiKey;
    }
    
    // Add user email header if available (required by backend for auth)
    if (userEmail) {
        defaultOptions.headers['X-User-Email'] = userEmail;
    }
    
    try {
        console.log(`📡 API Call: ${options.method || 'GET'} ${url}`);
        
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.warn(`⏰ Request timeout after ${timeout}ms`);
            controller.abort();
        }, timeout);
        
        const fetchOptions = { 
            ...defaultOptions, 
            ...options,
            signal: controller.signal 
        };
        
        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            let errorData = {};
            try {
                const text = await response.text();
                try {
                    errorData = JSON.parse(text);
                } catch {
                    errorData = { detail: text };
                }
            } catch (e) {
                // Response might not be readable
            }
            
            console.error(`❌ API Error: ${response.status}`, errorData);
            
            // Handle specific error codes
            if (response.status === 402) {
                throw new Error('INSUFFICIENT_CREDITS');
            }
            if (response.status === 401) {
                throw new Error('Not authenticated. Please log in from your dashboard first.');
            }
            if (response.status === 404) {
                throw new Error(`Endpoint not found: ${endpoint}. Please check if the backend API is properly configured.`);
            }
            if (response.status === 422) {
                // Validation error - show details
                console.error('❌ Validation Error (422) - Full details:', JSON.stringify(errorData, null, 2));
                const validationMsg = errorData.detail || 'Invalid request parameters';
                throw new Error(`Validation error: ${typeof validationMsg === 'string' ? validationMsg : JSON.stringify(validationMsg)}`);
            }
            if (response.status === 520) {
                throw new Error('Server error (520). The backend server may be overloaded. Please try again.');
            }
            if (response.status === 504 || response.status === 502) {
                throw new Error('Gateway timeout. The request took too long. Please try again.');
            }
            
            throw new Error(errorData.detail || errorData.message || `Request failed: ${response.status}`);
        }
        
        return response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error('Request timed out. Please try again.');
        }
        
        // Network errors (CORS, offline, etc.)
        if (error.message === 'Failed to fetch') {
            console.error('❌ Network error - possible causes: CORS, offline, or server unreachable');
            throw new Error('Network error: Unable to reach the server. Please check your connection and try again.');
        }
        
        throw error;
    }
};

// ==================== VIDEO PRODUCTION PROMPT GENERATION ====================

/**
 * Generate detailed video production prompts from the backend
 * This provides cinematic prompts, camera settings, and action timelines for shot pre-production
 * 
 * @param {object} params - Parameters for prompt generation
 * @param {string} params.shotDescription - Description of the shot
 * @param {string} params.characterName - Name of the main character in the shot
 * @param {string} params.sceneContext - Context/description of the scene
 * @param {number} params.durationSeconds - Duration of the shot in seconds
 * @param {string} params.aspectRatio - Aspect ratio (e.g., "16:9", "9:16", "1:1")
 * @param {string} params.mood - Mood of the shot
 * @param {string} params.genre - Genre of the project
 * @returns {Promise<object>} - Production prompt with video_prompt, camera, character_actions
 */
/**
 * Generate pre-production data for a single scene's shots.
 * Returns enriched shot data with cinematic prompts, actions, audio, dialogues.
 */
export const generatePreProduction = async (scene, script, characters, elements, projectMetadata) => {
    try {
        console.log(`📋 Generating pre-production for scene ${scene.id} (${scene.shots?.length || 0} shots)...`);

        // Feb 2026 (Option B) — Attach the References-to-video model
        // metadata so the admin backend can log/validate/tune. Omitted
        // when no model was picked.
        const videoGenPayload = buildVideoGenerationPayload();

        // Feb 2026 — Capability-based model + cascading fallback for the
        // pre-production reasoning LLM. See modelCascadeResolver.js.
        const preprodCascade = buildCapabilityFallbackChain('pre_production', [
            'pre_production', 'Pre Production', 'preproduction', 'shot_breakdown',
        ]);

        const body = {
            scene,
            script: script || '',
            characters: characters || [],
            elements: elements || [],
            project_metadata: projectMetadata || {},
            // Ad creator shot-breakdown rules — see
            // /app/docs/SHOT_GENERATION_RULES_SPEC.md for the contract.
            // The backend MAY ingest `shot_breakdown_rules` directly into
            // its LLM system prompt, or ignore the field for backward
            // compatibility. Either way, we always send it.
            shot_breakdown_rules: buildShotBreakdownRules(resolveShotDurationSeconds().seconds),
        };
        if (videoGenPayload) body.video_generation = videoGenPayload;
        if (preprodCascade.modelKey)                    body.model_key       = preprodCascade.modelKey;
        if (preprodCascade.fallbackModels?.length)      body.fallback_models = preprodCascade.fallbackModels;

        const result = await apiCall('/ai/generate-preproduction-async', {
            method: 'POST',
            body: JSON.stringify(body)
        }, 30000);
        
        const jobId = result.job_id || result.jobId;
        
        if (jobId) {
            console.log(`🔄 Pre-production job submitted, polling: ${jobId}`);
            // 30 attempts × 5s = 150s. Backend should now finish in ~20-40s with scene-scoped payload.
            const completed = await pollForJobResult(jobId, 30, 5000);
            
            const shots = completed.result?.shots || completed.shots || [];
            console.log(`✅ Pre-production complete for scene ${scene.id}: ${shots.length} shots`);
            return { success: true, shots, jobId };
        }
        
        // Immediate response (non-async)
        const shots = result.result?.shots || result.shots || [];
        return { success: true, shots };
        
    } catch (error) {
        console.error(`❌ Pre-production failed for scene ${scene.id}:`, error.message);
        return { success: false, error: error.message };
    }
};


/**
 * Feb 2026 — Expand a manually-created shot from a plain-text user
 * description into a fully-formatted cinematic shot description with
 * exactly N panels (1-8) and all 7 panel fields (duration, story purpose,
 * visual composition, main elements, camera movement, text on screen,
 * voice over).
 *
 * Reuses the pre-production async endpoint by submitting a synthetic
 * one-shot scene whose only shot carries the user's raw description.
 * The `shot_breakdown_rules.desired_panel_count` override pins the exact
 * panel count so the LLM produces N panels rather than the default 4-6.
 *
 * @param {string} rawDescription — user's typed / narrated description
 * @param {number} numPanels — desired panel count (1-8)
 * @param {object} options
 * @param {Array}  options.characters — full projectData.characters array
 * @param {Array}  options.elements — element subset (or empty if resolver handles)
 * @param {object} options.projectMetadata — project metadata for LLM context
 * @param {string} options.sceneTitle — optional scene context
 * @returns {Promise<{ success: boolean, shot?: object, error?: string }>}
 */
export const expandManualShot = async (rawDescription, numPanels, options = {}) => {
    try {
        const {
            characters = [],
            elements = [],
            shotCharacters = null,
            shotElements = null,
            projectMetadata = {},
            sceneTitle = '',
        } = options;
        if (!rawDescription || !rawDescription.trim()) {
            return { success: false, error: 'Description is empty' };
        }
        const N = Math.max(1, Math.min(8, Math.round(numPanels || 4)));
        console.log(`📋 Expanding manual shot → ${N} panels from user description (${rawDescription.length} chars)`);

        const shotDurationSeconds = resolveShotDurationSeconds().seconds;
        const videoGenPayload = buildVideoGenerationPayload();
        const preprodCascade = buildCapabilityFallbackChain('pre_production', [
            'pre_production', 'Pre Production', 'preproduction', 'shot_breakdown',
        ]);

        // Synthetic single-shot scene — backend treats it identically to
        // any real scene, so the same LLM + rules apply.
        const syntheticShotId = `manual-shot-${Date.now()}`;
        const scene = {
            id: `manual-scene-${Date.now()}`,
            title: sceneTitle || 'Manual shot',
            shots: [{
                id: syntheticShotId,
                description: rawDescription.trim(),
                characterIds: [],
                elementIds: [],
            }],
        };

        const body = {
            scene,
            script: rawDescription.trim(),
            characters: characters || [],
            elements: elements || [],
            project_metadata: projectMetadata || {},
            shot_breakdown_rules: buildShotBreakdownRules(shotDurationSeconds, N),
            is_manual_shot_expansion: true, // Diagnostic flag for admin backend logs
        };
        if (videoGenPayload) body.video_generation = videoGenPayload;
        if (preprodCascade.modelKey)                    body.model_key       = preprodCascade.modelKey;
        if (preprodCascade.fallbackModels?.length)      body.fallback_models = preprodCascade.fallbackModels;

        const result = await apiCall('/ai/generate-preproduction-async', {
            method: 'POST',
            body: JSON.stringify(body),
        }, 30000);

        const jobId = result.job_id || result.jobId;
        let returnedShot = null;
        if (jobId) {
            const completed = await pollForJobResult(jobId, 30, 5000);
            const shots = completed.result?.shots || completed.shots || [];
            if (shots.length > 0) returnedShot = shots[0];
        } else {
            const shots = result.result?.shots || result.shots || [];
            if (shots.length > 0) returnedShot = shots[0];
        }
        if (!returnedShot) {
            return { success: false, error: 'Backend returned no shots' };
        }

        // Feb 2026 — Manual-shot expansion contract:
        // The shot's `description` field MUST end up as a full cinematic
        // breakdown (opener paragraph + N "Panel N (mm:ss.s - mm:ss.s):"
        // blocks) so the panel parser can immediately extract N panels
        // for the storyboard UI. We first check whether the backend already
        // emitted such a description; if not, we synthesise one from the
        // returned `preProduction.character_actions` timeline.
        // Also extracts screenplay-style dialogues (CUE + line) so the
        // preproduction modal picks them up.
        const { description: finalDescription, dialogues: parsedDialogues } = ensureCinematicShotDescription({
            shot: returnedShot,
            rawDescription: rawDescription.trim(),
            numPanels: N,
            totalSeconds: shotDurationSeconds,
            characters: Array.isArray(shotCharacters) && shotCharacters.length ? shotCharacters : (characters || []),
            elements:   Array.isArray(shotElements)   && shotElements.length   ? shotElements   : (elements   || []),
            projectCharacters: characters || [],
        });

        // Diagnostic: how many panels does the final description contain?
        const parsedCount = parsePanelsFromDescription(finalDescription).length;
        console.log(`✅ Manual-shot expansion complete — description carries ${parsedCount} panel${parsedCount === 1 ? '' : 's'}, ${parsedDialogues.length} dialogue line${parsedDialogues.length === 1 ? '' : 's'} (requested ${N}).`);

        // Feb 2026 — Merge parsed dialogues into shot.dialogues[] so the
        // pre-production modal picks them up. Backend-supplied dialogues
        // (if any) take precedence via a de-dupe on (characterId, text).
        const backendDialogues = Array.isArray(returnedShot.dialogues) ? returnedShot.dialogues : [];
        const seenKeys = new Set(backendDialogues.map(d =>
            `${d.characterId || d.characterName || ''}|${(d.text || '').trim().toLowerCase()}`
        ));
        const mergedDialogues = [
            ...backendDialogues,
            ...parsedDialogues.filter(d => !seenKeys.has(
                `${d.characterId || d.characterName || ''}|${(d.text || '').trim().toLowerCase()}`
            )),
        ];

        // Auto-attach speaking character IDs to shot.characterIds so the
        // storyboard UI shows the right chips out of the box.
        const existingCharIds = Array.isArray(returnedShot.characterIds) ? returnedShot.characterIds : [];
        const speakingIds = mergedDialogues.map(d => d.characterId).filter(Boolean);
        const attachedIds = Array.from(new Set([...existingCharIds, ...speakingIds]));

        // Overwrite description on the returned shot so the caller can
        // merge it wholesale onto their local shot state.
        const shotWithDescription = {
            ...returnedShot,
            description: finalDescription,
            dialogues: mergedDialogues,
            characterIds: attachedIds,
        };
        return { success: true, shot: shotWithDescription, jobId };
    } catch (error) {
        console.error('❌ expandManualShot failed:', error.message);
        return { success: false, error: error.message };
    }
};


export const generateVideoProductionPrompt = async ({
    shotDescription,
    characterName,
    sceneContext,
    durationSeconds,
    aspectRatio = '16:9',
    mood,
    genre,
    startFrameImage,
    endFrameImage
}) => {
    try {
        console.log('🎬 Generating video production prompt...');
        console.log('  - Shot description:', shotDescription?.substring(0, 100) + '...');
        console.log('  - Character:', characterName);
        console.log('  - Duration:', durationSeconds, 'seconds');
        console.log('  - Start frame:', startFrameImage ? '✅ provided' : '❌ none');
        console.log('  - End frame:', endFrameImage ? '✅ provided' : '❌ none');
        
        const body = {
            shot_description: shotDescription,
            character_name: characterName,
            scene_context: sceneContext,
            duration_seconds: durationSeconds,
            aspect_ratio: aspectRatio,
            mood: mood,
            genre: genre
        };
        
        // Include frame images so backend AI can analyze visual content
        if (startFrameImage) body.start_frame_image = startFrameImage;
        if (endFrameImage) body.end_frame_image = endFrameImage;
        
        const result = await apiCall('/ai/generate-video-production-prompt', {
            method: 'POST',
            body: JSON.stringify(body)
        });
        
        console.log('✅ Video production prompt generated successfully');
        console.log('📋 Full API response:', JSON.stringify(result, null, 2));
        
        return result.production_prompt;
    } catch (error) {
        console.error('❌ Failed to generate video production prompt:', error);
        throw error;
    }
};

// ==================== VIDEO GENERATION ====================

/**
 * Poll for video job completion status
 * Video generation can take longer than images, so we use different defaults
 * @param {string} jobId - Job ID to poll for
 * @param {number} maxAttempts - Maximum polling attempts (default 60 = 10 minutes at 10s intervals)
 * @param {number} intervalMs - Polling interval in milliseconds (default 10000 = 10 seconds)
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<object>} - Job result when completed
 */
const pollForVideoJobResult = async (jobId, maxAttempts = 120, intervalMs = 5000, onProgress = null) => {
    console.log(`🔄 Starting to poll for job ${jobId}...`);
    const startTime = Date.now();
    // Track consecutive transient HTTP failures (504/502/network). A legit
    // upstream outage should stop after ~5 in a row instead of hammering
    // the poll endpoint for the full 120 attempts. Job-state failures
    // (status: "failed") are hard-terminal, handled separately below.
    let consecutiveTransientErrors = 0;
    const MAX_CONSECUTIVE_TRANSIENT = 5;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const job = await apiCall(`/ai/jobs/${jobId}`, { method: 'GET' });
            const elapsed = Math.round((Date.now() - startTime) / 1000);

            console.log(`📊 Poll attempt ${attempt}/${maxAttempts} (${elapsed}s): status=${job.status}`);

            if (onProgress) {
                onProgress(`Processing... ${elapsed}s elapsed (attempt ${attempt})`);
            }

            if (job.status === 'completed' || job.status === 'success') {
                // Extract video URL from all possible locations
                const videoUrl = job.video_url
                    || job.result?.video_url
                    || job.result?.result?.video?.url
                    || job.result?.result?.video_url
                    || parseVideoUrl(job);

                return {
                    success: true,
                    video_url: videoUrl,
                    videoUrl: videoUrl,
                    credits_used: job.credits_used || job.credits_cost || 0,
                    credits_remaining: job.credits_remaining,
                    job_id: jobId,
                    result: job.result
                };
            }

            if (job.status === 'failed' || job.status === 'error') {
                // Feb 13 2026 — Job-state failures MUST be hard-terminal.
                // Previously we threw a plain Error and the catch block
                // below used substring matching (`.includes('failed')`)
                // to decide whether to retry. That heuristic misses
                // failures whose `job.error` doesn't contain the word
                // "failed" (e.g., "Admin Backend returned 504") and so
                // the poll retried indefinitely. Flagging the error with
                // `isJobFailure` mirrors pollForJobResult and lets the
                // catch block short-circuit cleanly.
                const detail = job.error || job.message || job.result?.error || 'Video generation failed';
                console.error(`❌ Video job ${jobId} reported failure:`, detail, '· full body:', job);
                const err = new Error(String(detail));
                err.isJobFailure = true;
                err.jobId        = jobId;
                err.jobBody      = job;
                throw err;
            }

            // Reset the transient counter on any successful poll (even
            // "pending"), so a brief upstream hiccup doesn't count against
            // a long-running job.
            consecutiveTransientErrors = 0;

            // Still processing — wait before next poll
            await new Promise(resolve => setTimeout(resolve, intervalMs));

        } catch (error) {
            // Hard-terminal: the backend marked the job failed. Do NOT retry.
            if (error.isJobFailure) {
                throw error;
            }
            // Transient network / gateway error (504, 502, "Failed to
            // fetch", timeout). Retry, but bail after too many in a row.
            consecutiveTransientErrors += 1;
            if (attempt >= maxAttempts || consecutiveTransientErrors >= MAX_CONSECUTIVE_TRANSIENT) {
                const reason = consecutiveTransientErrors >= MAX_CONSECUTIVE_TRANSIENT
                    ? `${consecutiveTransientErrors} consecutive polling errors`
                    : `${attempt} attempts`;
                throw new Error(`Video generation polling failed after ${reason}: ${error.message}`);
            }
            console.warn(`⚠️ Poll error (attempt ${attempt}, transient #${consecutiveTransientErrors}), retrying:`, error.message);
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
    }

    throw new Error(`Video generation timed out after ${Math.round(maxAttempts * intervalMs / 1000)} seconds`);
};

/**
 * Parse video URL from various response formats
 */
function parseVideoUrl(result) {
    let videoUrl = null;
    
    // Deep nested: result.result.result.video_url
    if (result.result?.result?.video_url) {
        videoUrl = result.result.result.video_url;
    } else if (result.result?.result?.url) {
        videoUrl = result.result.result.url;
    }
    // Standard nested: result.result.video_url
    else if (result.result?.video_url) {
        videoUrl = result.result.video_url;
    } else if (result.result?.url) {
        videoUrl = result.result.url;
    }
    // Direct: result.video_url
    else if (result.video_url) {
        videoUrl = result.video_url;
    } else if (result.url) {
        videoUrl = result.url;
    }
    // Also check for 'output' field (some providers use this)
    else if (result.output?.video_url) {
        videoUrl = result.output.video_url;
    } else if (result.output?.url) {
        videoUrl = result.output.url;
    }
    // Check data field
    else if (result.data?.video_url) {
        videoUrl = result.data.video_url;
    } else if (result.data?.url) {
        videoUrl = result.data.url;
    }
    
    return videoUrl;
}

/**
 * Generate a video using AI models (with async polling support)
 * @param {string} prompt - Text prompt describing the video
 * @param {number} duration - Duration in seconds (5 or 10)
 * @param {string} style - Video style/model key
 * @param {string} aspectRatio - Aspect ratio (16:9, 9:16, 1:1)
 * @param {string} imageUrl - Optional start frame image URL
 * @param {string} endImageUrl - Optional end frame image URL
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<{success: boolean, videoUrl: string, jobId: string, creditsUsed: number}>}
 */
export const generateVideo = async (prompt, duration, style, aspectRatio, imageUrl = null, endImageUrl = null, onProgress = null, dialoguesData = null, cinematicActions = null, referenceImages = null) => {
    // Build request body once — model_key will be swapped per attempt
    const buildRequestBody = (modelKey) => {
        const body = {
            prompt,
            duration,
            model_key: modelKey,
            aspect_ratio: aspectRatio
        };
        // References-to-video mode (panel-based shots, Feb 2026):
        // when `referenceImages` is non-empty, the ordered array REPLACES
        // the legacy start/end frame pair entirely. The backend reads
        // `reference_images` and uses them as ordered keyframe references.
        // We deliberately omit start_frame_image / end_frame_image to avoid
        // sending two competing representations of the same shot.
        if (Array.isArray(referenceImages) && referenceImages.length > 0) {
            body.reference_images = referenceImages;
        } else {
            if (imageUrl) body.start_frame_image = imageUrl;
            if (endImageUrl) body.end_frame_image = endImageUrl;
        }
        if (dialoguesData && dialoguesData.length > 0) body.dialogues = dialoguesData;
        if (cinematicActions && cinematicActions.length > 0) body.cinematic_actions = cinematicActions;
        return body;
    };

    // Attempt video generation with a specific model
    const attemptGeneration = async (modelKey, attemptLabel) => {
        if (onProgress) onProgress(`${attemptLabel}: Submitting request...`);
        console.log(`🎬 [${attemptLabel}] Generating video with model: ${modelKey}`);

        const requestBody = buildRequestBody(modelKey);
        // Diagnostic: surface frame URLs (or their absence) at the API boundary
        console.log(`🎬 [${attemptLabel}] Request body keys:`, Object.keys(requestBody));
        if (Array.isArray(requestBody.reference_images) && requestBody.reference_images.length > 0) {
            console.log(`   ▸ reference_images: ${requestBody.reference_images.length} images (panel order)`);
            requestBody.reference_images.forEach((url, i) => {
                const s = String(url || '');
                const preview = s.startsWith('data:') ? `(base64, ${s.length} chars)` : s.substring(0, 80);
                console.log(`       [${i}] ${preview}`);
            });
        } else {
            console.log('   ▸ reference_images: (NOT SENT)');
        }
        if (requestBody.start_frame_image) {
            const sf = String(requestBody.start_frame_image);
            console.log(`   ▸ start_frame_image: ${sf.startsWith('data:') ? `(base64, ${sf.length} chars)` : sf.substring(0, 100)}`);
        } else {
            console.log('   ▸ start_frame_image: (NOT SENT)');
        }
        if (requestBody.end_frame_image) {
            const ef = String(requestBody.end_frame_image);
            console.log(`   ▸ end_frame_image:   ${ef.startsWith('data:') ? `(base64, ${ef.length} chars)` : ef.substring(0, 100)}`);
        } else {
            console.log('   ▸ end_frame_image:   (NOT SENT)');
        }

        const result = await apiCall('/ai/generate-video-async', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        }, 30000);

        const jobId = result.job_id || result.result?.job_id;

        if (!jobId) {
            const immediateUrl = parseVideoUrl(result);
            if (immediateUrl) {
                return { success: true, videoUrl: immediateUrl, creditsUsed: result.credits_used || 0, creditsRemaining: result.credits_remaining };
            }
            throw new Error('No job_id returned from video generation endpoint.');
        }

        if (onProgress) onProgress(`${attemptLabel}: Generating video...`);
        let completed;
        try {
            // Feb 2026 — 200 attempts × 5s = 1000s (~16.6 min).
            // Bumped from 150 (750s) after wan3-ref2video timed out even
            // at the 750s cap. minimax-ref2video, wan-*, and seedance 2.5
            // ref2video can all stretch past 900s under load. 16.6 min
            // gives headroom without letting a hung job block the FE
            // forever.
            completed = await pollForVideoJobResult(jobId, 200, 5000, (progress) => {
                if (onProgress) {
                    const elapsed = (typeof progress === 'object' ? progress.attempt : 0) * 5;
                    onProgress(`${attemptLabel}: Generating video... ${elapsed}s`);
                }
            });
        } catch (pollErr) {
            pollErr.jobId = jobId;
            throw pollErr;
        }

        const videoUrl = completed.video_url || completed.videoUrl;
        if (!videoUrl) throw new Error('Video job completed but no URL returned.');

        console.log(`✅ [${attemptLabel}] Video generated: ${videoUrl.substring(0, 80)}`);
        return { success: true, videoUrl, jobId, creditsUsed: completed.credits_used || 0, creditsRemaining: completed.credits_remaining };
    };

    // Get fallback models ordered by pricing tier: economy → standard → premium.
    // Capability key MUST match the primary model's capability so fallbacks
    // can also consume the same payload. In particular, when the user runs a
    // panel-based shot with `reference_images[]`, the fallback chain must
    // pull from "references to video" models — NOT image-to-video, which
    // would silently ignore the panels and render off the prompt alone.
    //
    // TIER CAP: We also read the user's selected tier from localStorage and
    // cap the fallback chain at-or-below that tier. Without this, a transient
    // failure on an Economy model silently escalates to Standard then Premium
    // — billing surprise + ignored tier selection (see bug Feb 28, 2026:
    // Economy + refs-to-video → ended up calling seedance2-ref2video).
    const getTierOrderedFallbackModels = () => {
        const isRefsToVideo = Array.isArray(referenceImages) && referenceImages.length > 0;
        const hasEndFrame = !!endImageUrl;
        let capKey;
        if (isRefsToVideo) {
            // Canonical capability key per AIToolkitSelector — see CAPABILITY_KEY_MAP
            // for legacy aliases ('references to video', 'referencesToVideo').
            capKey = 'references-to-video';
        } else if (hasEndFrame) {
            capKey = 'start-end-frame videos';
        } else {
            capKey = 'Image to video';
        }
        const tierCap = getSelectedTier();
        if (tierCap) {
            console.log(`🔒 Tier cap engaged: fallback chain limited to "${tierCap}" and below`);
        }
        return getModelsInFallbackOrder(capKey, style, tierCap);
    };

    try {
        // Attempt 1: Try the requested model — single attempt only.
        // Feb 2026 — Removed the "auto-retry same model on transient failure"
        // shim. Per user request: on ANY failure, fall through to the tier-
        // ordered fallback chain rather than burning time re-hitting a model
        // that just failed. INSUFFICIENT_CREDITS / UNAUTHORIZED are still
        // treated as fatal and abort the chain (below).
        return await attemptGeneration(style, `Model: ${style}`);
    } catch (primaryError) {
        console.warn(`⚠️ Primary model "${style}" failed:`, primaryError.message);

        if (primaryError.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Please purchase more credits to continue.');
        }
        if (primaryError.message === 'UNAUTHORIZED') {
            throw new Error('Not authenticated. Please log in from your dashboard.');
        }

        // Attempt 2+: Try fallback models in tier order (economy → standard → premium)
        if (onProgress) onProgress('Primary model failed. Waiting for your approval to try a fallback…');
        const fallbackModels = getTierOrderedFallbackModels();

        console.log(`🔄 Fallback order (economy→standard→premium):`, fallbackModels.map(m => `${m.key} [${m.tier}]`));

        // Feb 2026 (Phase 2) — recompute the capability label locally so
        // the approval dialog shows an accurate context string.
        const _isRefsToVideo = Array.isArray(referenceImages) && referenceImages.length > 0;
        const _hasEndFrame = !!endImageUrl;
        const _capabilityLabel = _isRefsToVideo
            ? 'References-to-video'
            : (_hasEndFrame ? 'Start-End-Frame video' : 'Image-to-video');

        let lastError = primaryError;
        let lastAttemptedKey = style;
        for (let i = 0; i < fallbackModels.length; i++) {
            const { key: fallbackKey, tier, credits, name } = fallbackModels[i];

            // Feb 2026 (Phase 2) — Approval gate before EVERY fallback attempt.
            // User picked 3a: shown once per failure, not once per whole chain.
            // If the user cancels, we abort the cascade.
            const approved = await requestFallbackApproval({
                failedModel: { key: lastAttemptedKey, error: lastError?.message || 'Model failed' },
                nextModel:   { key: fallbackKey, tier, credits, name: name || fallbackKey },
                attemptNumber: i + 1,
                totalAttempts: fallbackModels.length,
                capabilityLabel: _capabilityLabel,
            });
            if (!approved) {
                throw new Error(`Video generation cancelled by user before trying fallback "${fallbackKey}".`);
            }

            try {
                console.log(`🔄 Trying fallback ${i + 1}/${fallbackModels.length}: ${fallbackKey} [${tier}]`);
                return await attemptGeneration(fallbackKey, `Fallback ${i + 1} [${tier}]: ${fallbackKey}`);
            } catch (fallbackError) {
                console.warn(`⚠️ Fallback "${fallbackKey}" [${tier}] failed:`, fallbackError.message);
                if (fallbackError.message === 'INSUFFICIENT_CREDITS') throw fallbackError;
                lastError = fallbackError;
                lastAttemptedKey = fallbackKey;
                continue;
            }
        }

        // If tier-ordered list was empty, try fetching models from API as last resort
        // If tier-ordered list was empty, try fetching models from API as last resort.
        // CRITICAL: when the user requested refs-to-video (panel-based shot), we
        // MUST NOT fall back to image-to-video / start-end-frame models — those
        // can't consume `reference_images[]` and the backend would silently
        // ignore the panel ordering. For refs-to-video we fail loudly instead.
        // Also: when a `tierCap` is set, we cannot safely use this list because
        // it has no tier annotations — fail loudly instead of silently
        // escalating to Premium.
        if (fallbackModels.length === 0) {
            const isRefsToVideo = Array.isArray(referenceImages) && referenceImages.length > 0;
            const tierCap = getSelectedTier();
            if (isRefsToVideo) {
                console.warn('⚠️ No refs-to-video fallback models in tier catalog — failing loudly (refusing to silently cross-capability fall back to image-to-video).');
            } else if (tierCap) {
                console.warn(`⚠️ No tier-capped fallback models for tier "${tierCap}" — failing loudly (refusing to silently escalate beyond user's tier).`);
            } else {
                console.log('⚠️ No tier data found, falling back to API model list...');
                try {
                    const hasEndFrame = !!endImageUrl;
                    const apiModels = hasEndFrame
                        ? await getStartEndFrameVideoModels()
                        : await getImageToVideoModels();
                    const remainingModels = (apiModels || []).map(m => m.key || m.model_key || m).filter(k => k && k !== style);

                    for (let i = 0; i < remainingModels.length; i++) {
                        // Feb 2026 (Phase 2) — approval gate for API-list
                        // last-resort fallbacks too.
                        const approved = await requestFallbackApproval({
                            failedModel: { key: style, error: primaryError?.message || 'All catalog models failed' },
                            nextModel:   { key: remainingModels[i], name: remainingModels[i] },
                            attemptNumber: i + 1,
                            totalAttempts: remainingModels.length,
                            capabilityLabel: hasEndFrame ? 'Start-End-Frame video (API list)' : 'Image-to-video (API list)',
                        });
                        if (!approved) {
                            throw new Error(`Video generation cancelled by user before trying API-list fallback "${remainingModels[i]}".`);
                        }
                        try {
                            return await attemptGeneration(remainingModels[i], `API Fallback ${i + 1}: ${remainingModels[i]}`);
                        } catch (e) {
                            if (e.message === 'INSUFFICIENT_CREDITS') throw e;
                            continue;
                        }
                    }
                } catch { /* exhausted */ }
            }
        }

        // All models exhausted
        throw new Error(`Video generation failed with all models. Primary: ${style}. Fallbacks tried: ${fallbackModels.map(m => m.key).join(', ') || 'none available'}.`);
    }
};

/**
 * Generate video with Kling model (via User Backend)
 */
export const generateVideoWithKling = async (prompt, imageUrl, endImageUrl, aspectRatio, duration, onProgress) => {
    return generateVideo(prompt, duration, 'kling', aspectRatio, imageUrl, endImageUrl, onProgress);
};

/**
 * Generate video with Wan 2.2 model (via User Backend)
 */
export const generateVideoWithWan = async (prompt, imageUrl, aspectRatio, resolution, onProgress) => {
    return generateVideo(prompt, 5, 'wan', aspectRatio, imageUrl, null, onProgress);
};

/**
 * Generate video with Sora model (via User Backend)
 */
export const generateVideoWithSora = async (prompt, imageUrl, onProgress, resolution) => {
    return generateVideo(prompt, 4, 'sora', '16:9', imageUrl, null, onProgress);
};

/**
 * Generate video with LTX model (via User Backend)
 */
export const generateVideoWithLTX = async (prompt, imageUrl, onProgress, resolution) => {
    return generateVideo(prompt, 5, 'ltx', '16:9', imageUrl, null, onProgress);
};

/**
 * Generate video with Minimax model (via User Backend)
 */
export const generateVideoWithMinimax = async (prompt, imageUrl, onProgress) => {
    return generateVideo(prompt, 5, 'minimax', '16:9', imageUrl, null, onProgress);
};

// ==================== IMAGE GENERATION ====================

/**
 * Poll for job completion status
 * @param {string} jobId - Job ID to poll for
 * @param {number} maxAttempts - Maximum polling attempts (default 30 = 5 minutes at 10s intervals)
 * @param {number} intervalMs - Polling interval in milliseconds (default 10000 = 10 seconds)
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<object>} - Job result when completed
 */
export const pollForJobResult = async (jobId, maxAttempts = 30, intervalMs = 10000, onProgress = null) => {
    console.log(`🔄 Starting to poll for job ${jobId}...`);

    // Feb 2026 — Slow models (e.g., seedance2-ref2image) can trigger
    // Cloudflare 524 gateway timeouts on the admin backend's upstream
    // call. When that happens the admin backend prematurely marks the
    // job as `status: failed` with an error like "Admin Backend returned
    // 524", even though its own internal retries subsequently succeed
    // and the images DO get generated. Treat such failures as transient
    // and keep polling — the admin backend flips the status back to
    // `completed` once its retry lands. Bail out only after
    // TRANSIENT_FAIL_CAP consecutive transient failures.
    const TRANSIENT_FAIL_RE = /\b(52[024]|503|502|504|gateway\s*timeout|gateway\s*error|timed?\s*out|timeout|retry|retrying|transient|temporarily)\b/i;
    const TRANSIENT_FAIL_CAP = 6; // ~30s of consecutive transient fails at 5s interval → give up
    let consecutiveTransientFails = 0;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const result = await apiCall(`/ai/jobs/${jobId}`, {
                method: 'GET'
            });
            
            console.log(`📊 Poll attempt ${attempt}/${maxAttempts}: status = ${result.status}`);
            
            if (onProgress) {
                onProgress({
                    attempt,
                    maxAttempts,
                    status: result.status,
                    progress: result.progress || Math.round((attempt / maxAttempts) * 100)
                });
            }
            
            if (result.status === 'completed' || result.status === 'success') {
                console.log('✅ Job completed successfully');
                return result;
            }
            
            if (result.status === 'failed' || result.status === 'error') {
                const errorMsg = result.error || result.message || 'Generation failed';

                // If the failure looks like a transient upstream/gateway
                // issue, don't give up yet — keep polling. The admin
                // backend often flips the status back to `completed`
                // after its own internal retry succeeds.
                if (TRANSIENT_FAIL_RE.test(errorMsg) && consecutiveTransientFails < TRANSIENT_FAIL_CAP) {
                    consecutiveTransientFails += 1;
                    console.warn(`⏳ Transient job failure (${consecutiveTransientFails}/${TRANSIENT_FAIL_CAP}) — "${errorMsg}". Continuing to poll in case admin backend recovers…`);
                    await new Promise(resolve => setTimeout(resolve, intervalMs));
                    continue;
                }

                console.error('❌ Job failed:', errorMsg);
                const err = new Error(errorMsg);
                err.isJobFailure = true;
                err.jobId = jobId;
                throw err;
            }
            
            // Job still processing — reset transient counter and wait
            consecutiveTransientFails = 0;
            console.log(`⏳ Job still processing, waiting ${intervalMs/1000}s before next poll...`);
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            
        } catch (error) {
            // Stop the loop on job-state failures; retry only transient network/polling errors.
            if (error.isJobFailure || attempt >= maxAttempts) {
                throw error;
            }
            console.warn(`⚠️ Poll error (attempt ${attempt}), retrying:`, error.message);
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
    }
    
    throw new Error(`Job timed out after ${maxAttempts * intervalMs / 1000} seconds`);
};

/**
 * Generate images using AI models (with async polling for long-running jobs)
 * @param {string} prompt - Text prompt describing the image
 * @param {number} count - Number of images to generate
 * @param {string} style - Image style / model key
 * @param {string} aspectRatio - Aspect ratio
 * @param {string[]} referenceImages - Optional reference image URLs
 * @param {boolean} useAsync - Use async endpoint with polling (default true for reliability)
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<{success: boolean, images: string[], jobId: string, creditsUsed: number}>}
 */
// Feb 13 2026 — Shared image-model resolver used by every image-gen call
// (generateImage, generateSceneImage, generatePortraitWithTryOn). The user's
// AI Settings pick wins first. If they didn't pick anything, we read the
// capability's priority-ordered first model from `aiCapabilityTiers` in
// localStorage — the SAME data the AI Settings UI shows as "Default: xxx".
// Also builds a `fallback_models[]` list so the admin backend cascades
// through the user-visible models on failure instead of its own hidden
// defaults (that's how the user got `gemini-3-pro-image-preview/edit`
// generated when their UI clearly said "Default: Nano Banana New").
//
// Returns { modelKey, fallbackModels, source } — modelKey is safe to send
// in the request body; fallbackModels is the array of subsequent-priority
// keys the admin can cascade through.
const IMG_REF_CAPABILITY_ALIASES = [
    'Image reference to image',
    'Image Reference to Image',
    'image_ref_to_image',
    'reference_image',
];
const TEXT_TO_IMAGE_CAPABILITY_ALIASES = [
    'Text to image',
    'Text to Image',
    'text_to_image',
    'image_generation',
    'professional_image',
];

export const resolveImageModelWithFallback = (initialModelKey, hasReferenceImages) => {
    // Feb 2026 (Phase 3) — Delegate to the shared tier-cascade resolver so
    // image gen follows the same tier-of-pick rules as the 7 text/LLM
    // endpoints (Premium pick → P→S→E, Standard pick → S→E→P, Economy
    // pick → E→S→P). The primary alias defines the capability name; the
    // remaining aliases are checked case-insensitively.
    const capAliases = hasReferenceImages ? IMG_REF_CAPABILITY_ALIASES : TEXT_TO_IMAGE_CAPABILITY_ALIASES;
    const primaryCap = capAliases[0];
    const otherCaps = capAliases.slice(1);

    let { modelKey, fallbackModels, source } = buildCapabilityFallbackChain(primaryCap, otherCaps);

    // Preserve legacy contract: if the caller explicitly passed a
    // `initialModelKey` and no user pick was resolved, use the caller's
    // value as primary and let the cascade fill fallbacks.
    if (initialModelKey && initialModelKey !== 'default' && (source === 'default-only' || source === 'catalog-tiered' || source === 'catalog-flat')) {
        // Caller had an explicit model in mind (e.g. per-shot override).
        // Rebuild fallback list with caller's key as primary.
        const rest = fallbackModels.filter(k => k && k.toLowerCase() !== initialModelKey.toLowerCase());
        modelKey = initialModelKey;
        fallbackModels = rest;
        source = 'caller-override';
    }

    console.log(`🎨 resolveImageModelWithFallback → ${modelKey} (source: ${source}) · fallbacks: [${fallbackModels.join(', ') || 'none'}]`);
    if (modelKey === 'default') {
        console.warn(`⚠️ resolveImageModelWithFallback: sending "default" — backend will pick its own last-resort model.`);
    }
    return { modelKey, fallbackModels, source };
};

export const generateImage = async (prompt, count = 1, style = 'default', aspectRatio = '16:9', referenceImages = [], useAsync = true, onProgress = null, extraFields = {}) => {
    try {
        // Feb 2026 — Inject the project-wide visual-style directive so
        // every image (character portraits, element sheets, storyboard
        // panels, scene images, editor refinements) matches the style
        // the user picked on the Story step (realistic / cartoon_animation
        // / pixar_3d / anime). Read from localStorage — populated by
        // App.js whenever `activeProject.videoStyle` changes. Guarded so
        // we never double-inject if the caller already prefixed the
        // same directive.
        prompt = applyVideoStyleDirective(prompt);

        // Feb 13 2026 — Use the shared resolver so text-to-image and
        // image-ref-to-image share the same priority-first fallback logic.
        const hasReferenceImages = referenceImages && referenceImages.length > 0;
        const resolved = resolveImageModelWithFallback(style, hasReferenceImages);
        const modelKey       = resolved.modelKey;
        const fallbackModels = resolved.fallbackModels;

        // Format request according to User Backend API spec
        const requestBody = {
            prompt,
            count,
            model_key: modelKey,  // Use model_key for dynamic model selection
            model: modelKey,      // Fallback for backends expecting 'model'
            style: modelKey,      // Fallback for backends expecting 'style'
            aspect_ratio: aspectRatio,
            // Feb 13 2026 — Explicit fallback list so the admin cascades
            // through the models the user actually sees in AI Settings
            // instead of using its own hidden defaults.
            fallback_models: fallbackModels,
            // Enable virtual try-on if explicitly set via extraFields or detected in prompt
            virtual_try_on: extraFields.virtual_try_on === true ||
                           prompt.includes('virtual try-on') || 
                           prompt.includes('costume reference') || 
                           prompt.includes('COSTUME INSTRUCTIONS')
        };
        
        // Only add reference_images if provided
        if (referenceImages && referenceImages.length > 0) {
            requestBody.reference_images = referenceImages;
        }

        // Merge any extra structured fields (face_reference_images, costume_reference_images, etc.)
        if (extraFields && typeof extraFields === 'object') {
            Object.entries(extraFields).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    requestBody[key] = value;
                }
            });
        }
        
        // Use async endpoint with polling for long-running jobs (more reliable)
        if (useAsync) {
            console.log('🎨 Generating image via ASYNC endpoint...', JSON.stringify(requestBody, null, 2));
            
            // Start async generation
            const asyncResult = await apiCall('/ai/generate-image-async', {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });
            
            console.log('📥 Async generation started:', asyncResult);
            
            const jobId = asyncResult.job_id;
            if (!jobId) {
                // Fallback: maybe the async endpoint returned the result directly
                if (asyncResult.images || asyncResult.result?.images || asyncResult.result?.image_url) {
                    console.log('✅ Async endpoint returned result directly');
                    return parseImageResult(asyncResult);
                }
                throw new Error('No job_id returned from async endpoint');
            }
            
            // Poll for result — 75 attempts × 5s = ~6.25 minutes max.
            // Feb 2026: bumped from 40 → 75 so slower models like
            // seedance2-ref2image (which routinely triggers upstream
            // 524s + internal retries before landing images) get
            // enough runway. Transient 524-style `status: failed`
            // responses are now non-terminal in `pollForJobResult` —
            // see the transient-fail guard there.
            const estimatedTime = asyncResult.estimated_time_seconds || 60;
            console.log(`⏱️ Estimated time: ${estimatedTime} seconds`);
            
            if (onProgress) {
                onProgress({ status: 'processing', message: `Starting generation (est. ${estimatedTime}s)...` });
            }
            
            try {
                const result = await pollForJobResult(jobId, 75, 5000, onProgress);
                return parseImageResult(result);
            } catch (pollError) {
                // Attach jobId to error so caller can re-poll later
                pollError.jobId = jobId;
                throw pollError;
            }
        }
        
        // Fallback: Use synchronous endpoint
        console.log('🎨 Generating image via SYNC endpoint...', JSON.stringify(requestBody, null, 2));
        
        const result = await apiCall('/ai/generate-image', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
        
        console.log('✅ Image generation response:', JSON.stringify(result, null, 2));
        return parseImageResult(result);
        
    } catch (error) {
        console.error('❌ Image generation failed:', error);
        
        if (error.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Please purchase more credits to continue.');
        }
        if (error.message === 'UNAUTHORIZED') {
            throw new Error('Not authenticated. Please log in from your dashboard.');
        }
        
        throw error;
    }
};

/**
 * Parse image result from various response formats
 */
function parseImageResult(result) {
    // Check for nested errors first
    if (result.result?.success === false || result.result?.error) {
        const errorMessage = result.result?.error || 'AI service error';
        console.error('❌ Backend returned error:', errorMessage);
        throw new Error(errorMessage);
    }
    
    if (result.result?.result?.success === false || result.result?.result?.error) {
        const errorMessage = result.result?.result?.error || 'AI service error';
        console.error('❌ Backend returned nested error:', errorMessage);
        throw new Error(errorMessage);
    }
    
    // Handle various response formats from backend
    let images = [];
    
    // Deep nested structure: result.result.result.image_url (from Admin Backend)
    if (result.result?.result?.image_url) {
        images.push(result.result.result.image_url);
    }
    if (result.result?.result?.video_url) {
        // Sometimes video_url contains image URL (as seen in the response)
        images.push(result.result.result.video_url);
    }
    if (result.result?.result?.images && Array.isArray(result.result.result.images)) {
        images.push(...result.result.result.images);
    }
    
    // Standard nested: result.result.image_url
    if (result.result?.image_url) {
        images.push(result.result.image_url);
    }
    if (result.result?.images && Array.isArray(result.result.images)) {
        images.push(...result.result.images);
    }
    if (result.result?.url) {
        images.push(result.result.url);
    }
    
    // Direct on result
    if (result.images && Array.isArray(result.images)) {
        images.push(...result.images);
    }
    if (result.image_url) {
        images.push(result.image_url);
    }
    if (result.url) {
        images.push(result.url);
    }
    
    // Filter out any undefined/null/duplicate values
    images = [...new Set(images.filter(img => img && typeof img === 'string' && img.length > 10))];
    
    if (images.length === 0) {
        console.error('❌ No valid images in response. Full response:', JSON.stringify(result));
        throw new Error(`No image generated - backend returned: ${JSON.stringify(result).substring(0, 300)}`);
    }
    
    console.log('  Job ID:', result.job_id);
    console.log('  Credits used:', result.credits_used);
    console.log('  Credits remaining:', result.credits_remaining);
    console.log('  Images found:', images);
    
    return {
        success: true,
        images: images,
        jobId: result.job_id,
        creditsUsed: result.credits_used || 0,
        creditsRemaining: result.credits_remaining
    };
}

/**
 * Generate a scene/frame image
 */
export const generateSceneImage = async (prompt, aspectRatio, entities = [], referenceImages = [], modelId = 'default') => {
    // Build SEPARATE reference image lists so the backend knows
    // which images are faces, which are costumes, and which are elements.

    const faceReferenceImages = [];
    const costumeReferenceImages = [];
    const elementReferenceImages = [];
    const entityInstructions = [];
    let hasCostumeReferences = false;
    let hasElementReferences = false;

    entities.forEach((entity, idx) => {
        const entityName = entity.name || `Entity ${idx + 1}`;
        const isElement = entity.category === 'element';
        const isCharacter = entity.category === 'character' || entity.category === 'narrator' || !entity.category;

        if (isElement) {
            if (entity.image) {
                elementReferenceImages.push(entity.image);
                entityInstructions.push(`The "${entityName}" MUST look EXACTLY like its reference image. Do not modify its appearance.`);
                hasElementReferences = true;
            }
        } else if (isCharacter) {
            // Costume images not sent separately — the character portrait already includes
            // the costume from Virtual Try-On done in Cast & World page
            if (entity.image) {
                faceReferenceImages.push(entity.image);
                entityInstructions.push(
                    `"${entityName}" face, likeness, and outfit MUST match the reference image provided. This portrait is ONLY for "${entityName}".`
                );
            }
        }
    });

    // Enhance prompt with per-character mapping
    let enhancedPrompt = prompt;
    if (entityInstructions.length > 0) {
        enhancedPrompt = [
            prompt,
            '',
            'CRITICAL CHARACTER & ELEMENT REFERENCE MAPPING (follow strictly):',
            ...entityInstructions,
            '',
            'IMPORTANT: Do NOT swap costumes or likenesses between characters.',
            'Each character must wear ONLY their own assigned costume.',
        ].join('\n');
    }

    // Combine all reference images (flat list for `reference_images` field)
    const allReferenceImages = [
        ...costumeReferenceImages,
        ...faceReferenceImages,
        ...elementReferenceImages,
        ...(referenceImages || []).filter(Boolean),
    ];

    console.log(`🎬 generateSceneImage: faces=${faceReferenceImages.length}, costumes=${costumeReferenceImages.length}, elements=${elementReferenceImages.length}, continuity=${(referenceImages||[]).length}`);

    // Pass structured references so the backend can distinguish face vs costume
    const extraFields = {
        face_reference_images: faceReferenceImages,
        costume_reference_images: costumeReferenceImages,
        virtual_try_on: hasCostumeReferences,
    };

    return generateImageWithCostumeSupport(enhancedPrompt, 1, modelId, aspectRatio, allReferenceImages, hasCostumeReferences, extraFields);
};

/**
 * Generate image with explicit costume reference support
 * This ensures the backend knows to prioritize costume references
 */
const generateImageWithCostumeSupport = async (prompt, count, style, aspectRatio, referenceImages, hasCostumeReferences = false, extraFields = {}) => {
    const result = await generateImage(
        prompt,
        count,
        style,
        aspectRatio,
        referenceImages,
        true, // useAsync
        null, // onProgress
        extraFields
    );
    return result;
};

/**
 * Edit an existing image
 */
export const editImage = async (baseImage, prompt, characters = [], referenceImages = [], maskImage = null, modelId = 'default', aspectRatio = null, onProgress = null) => {
    try {
        // Feb 2026 — Image-editor refinements must respect the project's
        // chosen video style so a "make her smile" tweak on a Pixar 3D
        // shot doesn't come back photoreal. Inject the same directive
        // used by generateImage / generatePortraitWithTryOn.
        prompt = applyVideoStyleDirective(prompt);

        // Get the selected model for image editing
        let selectedModelKey = modelId;
        const selectedImageEditModel = getModelForCapability('image_ref_to_image') || 
                                       getModelForCapability('Image reference to image') ||
                                       getModelForCapability('image_editing');
        if (selectedImageEditModel && modelId === 'default') {
            selectedModelKey = selectedImageEditModel;
            console.log('🎨 Using selected image editing model:', selectedModelKey);
        }

        // Upload mask to GCS if it's base64 — fal.ai needs a hosted URL, not raw base64
        let maskUrl = maskImage;
        if (maskImage && maskImage.startsWith('data:')) {
            if (onProgress) onProgress('Uploading mask image...');
            try {
                const userEmail = sessionStorage.getItem('userEmail');
                const { uploadDataUriToBackend } = await import('./backendService');
                const gcsUrl = await uploadDataUriToBackend(maskImage, userEmail);
                if (gcsUrl) {
                    maskUrl = gcsUrl;
                    console.log('✅ Mask uploaded to GCS:', maskUrl.substring(0, 80));
                }
            } catch (e) {
                console.warn('⚠️ Mask upload to GCS failed, sending base64:', e.message);
            }
        }

        // Upload base64 reference images to GCS — fal.ai needs hosted URLs
        const uploadedRefs = [];
        if (referenceImages && referenceImages.length > 0) {
            if (onProgress) onProgress('Uploading reference images...');
            const userEmail = sessionStorage.getItem('userEmail');
            const { uploadDataUriToBackend } = await import('./backendService');
            for (const ref of referenceImages) {
                if (ref && ref.startsWith('data:')) {
                    try {
                        const gcsUrl = await uploadDataUriToBackend(ref, userEmail);
                        if (gcsUrl) {
                            uploadedRefs.push(gcsUrl);
                            console.log('✅ Reference image uploaded to GCS:', gcsUrl.substring(0, 80));
                        } else {
                            uploadedRefs.push(ref);
                        }
                    } catch (e) {
                        console.warn('⚠️ Ref upload to GCS failed, sending base64:', e.message);
                        uploadedRefs.push(ref);
                    }
                } else if (ref) {
                    uploadedRefs.push(ref);
                }
            }
        }

        // Collect only character/actor portrait references (no costumes, no elements)
        const actorRefs = [];
        characters.forEach(c => {
            if (c.image && (c.category === 'character' || c.category === 'narrator' || !c.category)) {
                actorRefs.push(c.image);
            }
        });

        // Build clean reference_images: deduplicate, exclude source frame, exclude temp/mask files, max 4
        const allCandidateRefs = [...uploadedRefs, ...actorRefs];
        const cleanRefs = [...new Set(allCandidateRefs)]
            .filter(url => url && typeof url === 'string')
            .filter(url => url !== baseImage)                    // Don't repeat source image
            .filter(url => !url.includes('temp_upload'))         // Exclude temp/mask files
            .filter(url => !url.startsWith('data:'))             // Exclude any remaining base64
            .slice(0, 4);                                        // Max 4 references

        // Build minimal request body per backend spec
        const body = {
            image_url: baseImage,
            prompt: aspectRatio ? `${prompt} Maintain the exact aspect ratio of ${aspectRatio}. Do not crop or change the dimensions.` : prompt,
        };
        if (maskUrl) body.mask_url = maskUrl;
        if (selectedModelKey && selectedModelKey !== 'default') body.model_key = selectedModelKey;
        if (aspectRatio) body.aspect_ratio = aspectRatio;
        if (cleanRefs.length > 0) body.reference_images = cleanRefs;

        if (onProgress) onProgress('Submitting image edit request...');

        console.log('🎨 Edit image request (async):', {
            model_key: body.model_key,
            has_mask: !!body.mask_url,
            reference_images_count: cleanRefs.length,
            reference_images: cleanRefs.map(r => r.substring(0, 80)),
            aspect_ratio: aspectRatio,
        });

        // Step 1: POST to async endpoint — returns immediately with job_id
        const result = await apiCall('/ai/edit-image-async', {
            method: 'POST',
            body: JSON.stringify(body)
        }, 30000); // 30s — should return job_id quickly

        console.log('📥 Edit-image-async response:', result);

        const jobId = result.job_id || result.result?.job_id;

        if (jobId) {
            // Step 2: Poll for completion every 5 seconds, up to 5 minutes
            console.log('🔄 Image edit job submitted, polling for result:', jobId);
            if (onProgress) onProgress('Image edit in progress...');

            const completed = await pollForJobResult(jobId, 60, 5000, (progress) => {
                if (onProgress && typeof progress === 'object') {
                    const elapsed = progress.attempt * 5;
                    onProgress(`Processing image... ${elapsed}s elapsed`);
                } else if (onProgress) {
                    onProgress(progress);
                }
            });

            // Extract image URL from polled result
            const pollImageUrl = completed.image_url
                || completed.result?.image_url
                || completed.result?.images?.[0]
                || completed.images?.[0]
                || completed.result?.result?.image_url;

            console.log('🎨 Polled image URL:', pollImageUrl);
            return {
                success: !!pollImageUrl,
                imageUrl: pollImageUrl,
                images: pollImageUrl ? [pollImageUrl] : [],
                jobId,
                creditsUsed: completed.credits_used || completed.credits_cost || 0
            };
        }

        // Fallback: endpoint returned result directly (no polling needed)
        let editedImageUrl = null;
        
        if (result.result?.image_url) {
            editedImageUrl = result.result.image_url;
        } else if (result.image_url) {
            editedImageUrl = result.image_url;
        } else if (result.images && result.images.length > 0) {
            editedImageUrl = result.images[0];
        } else if (result.result?.images && result.result.images.length > 0) {
            editedImageUrl = result.result.images[0];
        } else if (result.data?.image_url) {
            editedImageUrl = result.data.image_url;
        } else if (result.output?.image_url) {
            editedImageUrl = result.output.image_url;
        }
        
        console.log('🎨 Extracted image URL:', editedImageUrl);
        
        return {
            success: !!editedImageUrl,
            imageUrl: editedImageUrl,
            images: editedImageUrl ? [editedImageUrl] : [],
            jobId: result.job_id,
            creditsUsed: result.credits_used
        };
        
    } catch (error) {
        console.error('❌ Image editing failed:', error);
        throw error;
    }
};

/**
 * Generate character portrait with virtual try-on support
 * @param {string} description - Character description
 * @param {Array} refImages - Face/likeness reference images
 * @param {Array} costumeImages - Costume/accessory reference images (for virtual try-on)
 * @param {string} makeup - Makeup/styling level
 * @param {string} aspectRatio - Output aspect ratio
 * @param {string} category - 'character', 'narrator', or 'element'
 * @param {string} modelId - AI model to use
 */
export const generateCharacterPortrait = async (description, refImages = [], costumeImages = [], makeup = '', aspectRatio = '1:1', category = 'character', modelId = 'default') => {
    // Build an enhanced prompt that instructs AI to use costume references for virtual try-on
    let enhancedPrompt = `${category} portrait: ${description}.`;
    
    // Add makeup/styling instruction
    if (makeup && makeup !== 'natural') {
        enhancedPrompt += ` Makeup/styling: ${makeup}.`;
    }
    
    // If costume images are provided, add virtual try-on instructions
    if (costumeImages && costumeImages.length > 0) {
        enhancedPrompt += ` IMPORTANT: Use the provided costume/accessory reference images for virtual try-on. The character MUST wear EXACTLY the outfit, clothing, and accessories shown in the costume reference images, regardless of any clothing described in the text. The costume reference images take priority over any text description of clothing.`;
    }
    
    // If face reference images are provided, add likeness instructions
    if (refImages && refImages.length > 0) {
        enhancedPrompt += ` Maintain the exact facial features, likeness, and identity from the face reference images.`;
    }
    
    console.log('🎨 Portrait generation prompt:', enhancedPrompt);
    console.log('📸 Face reference images:', refImages?.length || 0);
    console.log('👗 Costume reference images:', costumeImages?.length || 0);
    
    // Use dedicated portrait generation with separate image categories
    return generatePortraitWithTryOn(
        enhancedPrompt,
        refImages,
        costumeImages,
        aspectRatio,
        modelId
    );
};

/**
 * Generate portrait with virtual try-on - sends face and costume images separately
 * Uses async polling pattern to avoid gateway timeouts on long-running generations.
 */
const generatePortraitWithTryOn = async (prompt, faceImages = [], costumeImages = [], aspectRatio = '1:1', modelId = 'default') => {
    try {
        console.log('🖼️ generatePortraitWithTryOn received aspectRatio:', aspectRatio, '(type:', typeof aspectRatio, ')');
        
        // Feb 2026 — Inject the project's video-style directive so
        // character portraits match the chosen visual style (realistic /
        // cartoon / pixar / anime). This path does NOT go through
        // `generateImage`, so the injection has to happen here too.
        prompt = applyVideoStyleDirective(prompt);

        const validAspectRatio = aspectRatio && aspectRatio !== '' ? aspectRatio : '16:9';
        console.log('🖼️ Using aspectRatio:', validAspectRatio);
        
        const hasAnyReferences = (faceImages?.length > 0) || (costumeImages?.length > 0);
        // Feb 13 2026 — Use the shared image-model resolver so this path
        // gets the same priority-first fallback as `generateImage`.
        const resolved = resolveImageModelWithFallback(modelId, hasAnyReferences);
        const modelKey       = resolved.modelKey;
        const fallbackModels = resolved.fallbackModels;
        
        const requestBody = {
            prompt,
            count: 1,
            model_key: modelKey,
            model: modelKey,
            fallback_models: fallbackModels,
            aspect_ratio: validAspectRatio,
            virtual_try_on: costumeImages && costumeImages.length > 0,
            face_reference_images: faceImages || [],
            costume_reference_images: costumeImages || [],
            reference_images: [...(costumeImages || []), ...(faceImages || [])]
        };
        
        console.log('🎨 Portrait request (async):', {
            prompt: prompt.substring(0, 100) + '...',
            model_key: modelKey,
            fallback_models: fallbackModels,
            aspect_ratio: validAspectRatio,
            virtual_try_on: requestBody.virtual_try_on,
            face_images_count: faceImages?.length || 0,
            costume_images_count: costumeImages?.length || 0
        });
        
        // Step 1: Submit via async endpoint (returns quickly with job_id)
        const asyncResult = await apiCall('/ai/generate-image-async', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        }, 30000); // 30s timeout — should return job_id fast
        
        console.log('📥 Portrait async job submitted:', asyncResult);
        
        const jobId = asyncResult.job_id;
        
        if (!jobId) {
            // Fallback: endpoint returned result directly (no polling needed)
            console.log('⚡ Portrait returned directly (no job_id), parsing result...');
            return parseImageResult(asyncResult);
        }
        
        // Step 2: Poll for completion (30 attempts × 10s = 5 min max)
        console.log(`🔄 Polling for portrait job ${jobId}...`);
        const result = await pollForJobResult(jobId, 30, 10000);
        return parseImageResult(result);
        
    } catch (error) {
        console.error('❌ Portrait generation failed:', error);
        throw error;
    }
};

// ==================== ELEMENT IMAGE GENERATION ====================

/**
 * Generate an image for an "Other Element" (prop, location, object)
 * This is separate from character portrait generation as elements don't have faces, costumes, or makeup
 * 
 * @param {string} description - Description of the element
 * @param {string[]} referenceImages - Reference images to guide the generation (what the element should look like)
 * @param {string} aspectRatio - Aspect ratio for the generated image
 * @param {string} modelId - Model ID to use for generation
 * @returns {Promise<{success: boolean, images: string[], jobId: string, creditsUsed: number}>}
 */
export const generateElementImage = async (description, referenceImages = [], aspectRatio = '1:1', modelId = 'default') => {
    try {
        // Build prompt specifically for element generation
        const enhancedPrompt = `Generate an image of: ${description}. IMPORTANT: This is an inanimate object/element/prop/location. Do NOT include any humans, people, animals, or living creatures in this image. Focus only on the object/element itself.${referenceImages.length > 0 ? ' The generated element MUST closely match the provided reference images in style, appearance, and details.' : ''}`;
        
        console.log('🎭 Element generation:');
        console.log('  - Description:', description.substring(0, 100) + '...');
        console.log('  - Reference images:', referenceImages.length);
        console.log('  - Aspect ratio:', aspectRatio);
        console.log('  - Model:', modelId);
        
        // Get appropriate model for image generation
        const allSelectedModels = getSelectedModels();
        let selectedModelKey = modelId;
        
        if (referenceImages.length > 0) {
            // Use image-reference model if we have references
            const refModelKeys = ['Image reference to image', 'image_ref_to_image', 'reference_image'];
            for (const key of refModelKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    selectedModelKey = model;
                    console.log('  - Using image-ref model:', selectedModelKey);
                    break;
                }
            }
        } else {
            // Use text-to-image model
            const textModelKeys = ['Text to image', 'image_generation', 'text_to_image'];
            for (const key of textModelKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    selectedModelKey = model;
                    console.log('  - Using text-to-image model:', selectedModelKey);
                    break;
                }
            }
        }
        
        // Use the standard generateImage function with reference images
        const result = await generateImage(
            enhancedPrompt,
            1,
            selectedModelKey,
            aspectRatio,
            referenceImages,
            true,  // useAsync
            null   // onProgress
        );
        
        console.log('✅ Element image generated successfully');
        return result;
        
    } catch (error) {
        console.error('❌ Element generation failed:', error);
        throw error;
    }
};

// ==================== AUDIO/TTS GENERATION ====================

/**
 * Detect ElevenLabs / upstream 429 (Too Many Requests) errors.
 * The message shape from the proxy backend looks like:
 *   "AI service error: Client error '429 Too Many Requests' for url '...'"
 * but we also match a bare "429" or a `status === 429` on the error object.
 */
const isRateLimitError = (err) => {
    if (!err) return false;
    if (err.status === 429 || err.statusCode === 429) return true;
    const msg = String(err.message || err).toLowerCase();
    return msg.includes('429') || msg.includes('too many requests') || msg.includes('rate limit');
};

const _ttsSleep = (ms) => new Promise(r => setTimeout(r, ms));

/**
 * Generate speech from text using AI
 * @param {string} text - Text to convert to speech
 * @param {string} voiceId - Voice ID to use
 * @returns {Promise<{success: boolean, audioUrl: string, jobId: string, creditsUsed: number}>}
 */
export const generateSpeech = async (text, voiceId) => {
    // Feb 2026 — Retry on 429 (Too Many Requests) with exponential backoff.
    // ElevenLabs caps concurrent TTS requests per plan (~2 for free/starter),
    // and auto-TTS in pre-production can fan out several lines at once.
    // Backoff: 1s → 2s → 4s + jitter (max 3 retries).
    const maxRetries = 3;
    let attempt = 0;
    const jitter = () => Math.floor(Math.random() * 250);

    while (true) {
        try {
            const raw = await apiCall('/ai/generate-speech', {
                method: 'POST',
                body: JSON.stringify({
                    text,
                    voice_id: voiceId
                })
            });

            // Feb 2026 — Tolerant response parser.
            // Backends may emit any of the following shapes:
            //   sync-flat:     { audio_url: "...", audio_data: "..." }
            //   sync-nested:   { result: { audio_url: "...", audio_data: "..." } }
            //   sync-data:     { data:   { audio_url: "..." } }
            //   async-queued:  { job_id: "…", status: "queued" }
            // We probe every known path AND, if only `job_id` came back with no
            // audio, we poll `/ai/jobs/{id}` until it completes.
            const pullAudio = (envelope) => {
                const candidates = [
                    envelope,
                    envelope?.result,
                    envelope?.result?.result,
                    envelope?.data,
                    envelope?.data?.result,
                ].filter(Boolean);
                for (const c of candidates) {
                    const url = c.audio_url || c.audioUrl || c.url;
                    const b64 = c.audio_data || c.audioData || c.audio_base64 || c.audio;
                    if (url || b64) return { audioUrl: url || null, audioData: b64 || null };
                }
                return { audioUrl: null, audioData: null };
            };

            let { audioUrl, audioData } = pullAudio(raw);

            // Async fallback — no audio in sync response but we got a job id.
            if (!audioUrl && !audioData && (raw?.job_id || raw?.jobId)) {
                const jobId = raw.job_id || raw.jobId;
                console.log(`🎙️ generate-speech is async (job ${jobId}) — polling…`);
                const completed = await pollForJobResult(jobId, 60, 3000);
                const picked = pullAudio(completed);
                audioUrl = picked.audioUrl;
                audioData = picked.audioData;
            }

            return {
                success: !!(audioUrl || audioData),
                audioUrl: audioUrl || null,
                audioData: audioData || null,
                jobId: raw?.job_id || raw?.jobId || null,
                creditsUsed: raw?.credits_used ?? raw?.result?.credits_used,
            };

        } catch (error) {
            if (isRateLimitError(error) && attempt < maxRetries) {
                const wait = (1000 * Math.pow(2, attempt)) + jitter();
                console.warn(`⏳ generate-speech rate-limited (429), retrying in ${wait}ms (attempt ${attempt + 1}/${maxRetries})…`);
                await _ttsSleep(wait);
                attempt++;
                continue;
            }
            console.error('❌ Speech generation failed:', error);
            throw error;
        }
    }
};

/**
 * Clone a voice from audio sample
 * @param {string} name - Name for the cloned voice
 * @param {string} audioData - Audio data (base64 or URL)
 * @returns {Promise<{success: boolean, voiceId: string}>}
 */
export const cloneVoice = async (name, audioData) => {
    try {
        const result = await apiCall('/ai/clone-voice', {
            method: 'POST',
            body: JSON.stringify({
                name,
                audio_data: audioData
            })
        });
        
        return {
            success: true,
            voiceId: result.result?.voice_id,
            jobId: result.job_id
        };
        
    } catch (error) {
        console.error('❌ Voice cloning failed:', error);
        throw error;
    }
};

/**
 * Get available voices
 */
export const getVoices = async (filters = {}) => {
    try {
        // Feb 2026 — support ElevenLabs voice library filters
        // (search / gender / age / accent / language / use_case / category).
        // Omit `filters` for the legacy "list everything" behaviour.
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(filters || {})) {
            if (v !== undefined && v !== null && String(v).trim() !== '') {
                params.set(k, String(v));
            }
        }
        const suffix = params.toString() ? `?${params.toString()}` : '';
        const result = await apiCall(`/ai/voices${suffix}`, {
            method: 'GET'
        });

        return result.voices || [];

    } catch (error) {
        console.error('❌ Failed to get voices:', error);
        return [];
    }
};

/**
 * Upload a voice sample (recorded blob or file) to the user backend which
 * pushes it to GCS and returns a public URL that can then be passed to
 * `cloneVoiceFromUrl`. Multipart form-data.
 *
 * @param {Blob|File} blob — audio file (mp3/wav/ogg/webm/m4a, ≤ 25 MB)
 * @param {string} [filename] — optional filename (defaults to sample.<ext>)
 * @returns {Promise<{ url: string, gs_url?: string }>}
 */
export const uploadVoiceSample = async (blob, filename) => {
    const apiUrl = getUserBackendUrl();
    const url = `${apiUrl}/ai/upload-voice-sample`;
    const apiKey = sessionStorage.getItem('apiKey');
    const userEmail = sessionStorage.getItem('userEmail');

    const fd = new FormData();
    const guessedExt = (blob?.type || '').split('/')[1] || 'webm';
    fd.append('file', blob, filename || `sample.${guessedExt}`);

    const headers = {};
    if (apiKey) headers['X-API-Key'] = apiKey;
    if (userEmail) headers['X-User-Email'] = userEmail;

    const resp = await fetch(url, {
        method: 'POST',
        body: fd,
        headers,
        credentials: 'include',
    });
    if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`upload-voice-sample failed (${resp.status}): ${text || resp.statusText}`);
    }
    const data = await resp.json();
    if (!data?.url) throw new Error('upload-voice-sample: backend returned no url');
    return { url: data.url, gs_url: data.gs_url };
};

/**
 * Clone a voice via ElevenLabs using an audio URL previously produced by
 * `uploadVoiceSample`. Costs 200 credits on success.
 *
 * @param {string} audioUrl — public URL of the uploaded sample
 * @param {string} voiceName — name to persist against the cloned voice
 * @returns {Promise<{ voiceId: string, creditsUsed?: number, jobId?: string }>}
 */
export const cloneVoiceFromUrl = async (audioUrl, voiceName) => {
    if (!audioUrl) throw new Error('cloneVoiceFromUrl: audio_url is required');
    const result = await apiCall('/ai/clone-voice', {
        method: 'POST',
        body: JSON.stringify({ audio_url: audioUrl, voice_name: voiceName || `Studio Pro voice ${Date.now()}` }),
    });
    const voiceId = result?.voice_id || result?.result?.voice_id;
    if (!voiceId) throw new Error('clone-voice: backend did not return voice_id');
    return {
        voiceId,
        creditsUsed: result?.credits_used,
        jobId: result?.job_id,
    };
};

/**
 * Delete a cloned voice from ElevenLabs. Call this before removing a
 * character record that owns a cloned voice so we free the ElevenLabs slot.
 *
 * @param {string} voiceId
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteClonedVoice = async (voiceId) => {
    if (!voiceId) return { success: false };
    try {
        const result = await apiCall(`/ai/voice/${encodeURIComponent(voiceId)}`, {
            method: 'DELETE',
        });
        return { success: !!result?.success };
    } catch (error) {
        console.warn('⚠️ deleteClonedVoice failed (non-fatal):', error.message);
        return { success: false };
    }
};

// ==================== TEXT GENERATION (Scripts, Prompts) ====================

/**
 * Generate script from concept
 * @param {string} concept - The story concept/idea
 * @param {string} style - Audio style: dialogue/narration/both/none
 * @param {string} region - Target region/country
 * @param {string} language - Dialogue/narration language
 * @param {Object} projectDetails - Project details from setup (name, duration, aspectRatio)
 */
export const generateScript = async (concept, style, region, language, projectDetails = {}) => {
    try {
        // Build request body with parameters the backend expects
        // Based on backend API: concept, duration, region, language, audio_style, style, tone
        const requestBody = {
            concept,
            audio_style: style,  // "dialogue", "narration", "both", or "none"
            region,
            language
        };
        
        // Add duration from project settings
        // Backend may expect duration as a number (seconds) or string
        if (projectDetails.duration) {
            let duration = projectDetails.duration;
            
            // Parse duration to extract numeric value
            // Handles formats: "< 15 seconds", "15 seconds", "1-3 minutes", etc.
            if (duration.includes('seconds')) {
                // Extract number from "< 15 seconds" or "15 seconds"
                const match = duration.match(/(\d+)\s*seconds/i);
                if (match) {
                    requestBody.duration = parseInt(match[1], 10); // Send as number
                }
            } else if (duration.includes('minute')) {
                // Extract from "< 1 minute", "1-3 minutes", etc.
                const match = duration.match(/(\d+)/);
                if (match) {
                    requestBody.duration = parseInt(match[1], 10) * 60; // Convert to seconds
                }
            } else {
                // Fallback - try to parse as number
                const num = parseInt(duration, 10);
                if (!isNaN(num)) {
                    requestBody.duration = num;
                }
            }
        }
        
        // Add optional parameters if available
        if (projectDetails.audioStyle) {
            requestBody.audio_style = projectDetails.audioStyle;
        }
        if (projectDetails.videoStyle) {
            // Visual style for the script's characters, props and world.
            // One of: 'realistic' | 'cartoon_animation' | 'pixar_3d' | 'anime'.
            // Backend uses this to steer the LLM's visual direction so the
            // script's descriptions match the intended visual style.
            requestBody.video_style = projectDetails.videoStyle;
        }
        if (projectDetails.videoPace) {
            // Feb 2026 — Video pace controls the LLM's shot cadence when it
            // structures scenes/shots to fit the target duration.
            // One of: 'slow' | 'standard' (default) | 'fast'.
            //   • slow     → fewer, longer, contemplative shots
            //   • standard → balanced cadence (LLM's default)
            //   • fast     → more, shorter, quick-cut shots
            // Backend can safely ignore unknown values for back-compat.
            requestBody.video_pace = projectDetails.videoPace;
        }
        if (projectDetails.tone) {
            requestBody.tone = projectDetails.tone;
        }

        // Industry / Video-Type / Cast context (Feb 2026).
        // Sent for the "I have a rough idea" path so the LLM can target the
        // script at the user's selected industry, video format and on-screen-
        // people policy. All three are optional; backend may safely ignore
        // them for backward compatibility.
        if (projectDetails.industry?.name) {
            requestBody.industry = {
                id: projectDetails.industry.id,
                name: projectDetails.industry.name,
                description: projectDetails.industry.desc,
                marketing_driver: projectDetails.industry.benefit,
            };
        }
        if (projectDetails.videoType?.name) {
            requestBody.video_type = {
                id: projectDetails.videoType.id,
                name: projectDetails.videoType.name,
                description: projectDetails.videoType.desc,
                goal: projectDetails.videoType.benefit,
            };
        }
        if (projectDetails.castMode) {
            // 'with_actors' | 'product_only'.
            // When 'product_only', the LLM must keep zero people on screen
            // (no actors, hands, faces, crowds) — off-screen narration IS
            // allowed.
            requestBody.cast_mode = projectDetails.castMode;
        }

        // Ad creator shot-generation rules — tell the script-writing LLM
        // that downstream the script will be sliced into 15-second shots, so
        // it should pace the script with clean 15-second beat breaks and aim
        // for a total runtime divisible by 15s. Same rule object also forwarded
        // to /ai/generate-storyboard-async and /ai/generate-preproduction-async.
        // See /app/docs/SHOT_GENERATION_RULES_SPEC.md for the contract.
        requestBody.shot_generation_rules = buildShotGenerationRules(resolveShotDurationSeconds().seconds);

        // Feb 2026 (Option B) — Forward the resolved References-to-video
        // model so the admin backend can log it, validate our
        // frontend-computed duration, or do model-family-specific prompt
        // tuning. Omitted entirely when no model is picked so the backend
        // treats it as opt-in.
        const videoGenPayload = buildVideoGenerationPayload();
        if (videoGenPayload) requestBody.video_generation = videoGenPayload;

        // Brand scope (Feb 2026): forwards saved-brand id + selected product/
        // service ids so the admin backend loads scoped context and prepends
        // it to the script-writing prompt. Backend spec:
        // PHASE_2_BRAND_PICKER_USER_BACKEND_GUIDE.md.
        if (projectDetails.brandId)     requestBody.brand_id     = projectDetails.brandId;
        if (Array.isArray(projectDetails.productIds) && projectDetails.productIds.length)
            requestBody.product_ids  = projectDetails.productIds;
        if (Array.isArray(projectDetails.serviceIds) && projectDetails.serviceIds.length)
            requestBody.service_ids  = projectDetails.serviceIds;

        console.log('📝 Script generation request:', JSON.stringify(requestBody, null, 2));

        // Feb 2026 — Wire capability-based model selection + cascading
        // fallback for the reasoning LLM. Uses the shared resolver so the
        // rules stay consistent across all text/LLM endpoints. Aliases
        // cover both the canonical capability key and legacy variants.
        const scriptCascade = buildCapabilityFallbackChain('script_generation', [
            'script_generation', 'Script Generation', 'generate_script', 'scriptwriter',
        ]);
        if (scriptCascade.modelKey)                      requestBody.model_key       = scriptCascade.modelKey;
        if (scriptCascade.fallbackModels?.length)        requestBody.fallback_models = scriptCascade.fallbackModels;

        const result = await apiCall('/ai/generate-script-async', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        }, 30000); // 30s — should return job_id quickly
        
        // Async path: poll if we got a job_id
        const jobId = result?.job_id || result?.result?.job_id;
        if (jobId) {
            console.log('🔄 Script generation returned job_id, polling:', jobId);
            const completed = await pollForJobResult(jobId, 120, 5000);
            const script = completed?.result?.result?.script || completed?.result?.script || completed?.script || '';
            console.log('✅ Script polled result:', { scriptLength: script?.length || 0, creditsUsed: completed?.credits_used });
            return { script, credits_used: completed?.credits_used || completed?.credits_cost || 0 };
        }
        
        // Immediate result (no polling needed)
        const script = result?.result?.result?.script || result?.result?.script || result?.script || '';
        
        console.log('✅ Script generation response:', {
            hasResult: !!result,
            hasNestedResult: !!result?.result,
            hasDoubleNestedResult: !!result?.result?.result,
            scriptLength: script?.length || 0,
            creditsUsed: result?.credits_used
        });
        
        return { script, credits_used: result?.credits_used || 0 };
        
    } catch (error) {
        console.error('❌ Script generation failed:', error);
        throw error;
    }
};


/**
 * Generate a batch of distinct video concepts via the admin backend.
 *
 * Endpoint: POST /api/ai/generate-concepts-async (proxied by user backend
 * which injects X-Internal-Service-Key server-side — never in browser).
 *
 * Request schema (flat):
 *   {
 *     projectIdea: string,          // rich, combined brief: brand + industry + video type + reference + duration
 *     count: number,                // 1..6, default 4
 *     genre: string,                // industry name (e.g. "Beauty Brands") — used by admin for stylistic bias
 *     language: string,             // default "English"
 *     model_key: string | undefined // optional. User's AI-Toolkit pick for the `generate_concepts` capability.
 *                                   // If undefined / unknown, admin falls back silently to Gemini 3 Pro (capability default).
 *   }
 *
 * Response:
 *   Immediate: { job_id, status: "processing", estimated_time_seconds }
 *   Polled:    { status: "completed", result: { concepts: [{title, hook, description}, ...], credits_used? } }
 *              (parser tolerates alternative nestings for safety)
 */
export const generateConcepts = async ({
    projectIdea,
    count = 4,
    genre = '',
    language = 'English',
    modelKey = undefined,
    referenceImage = undefined,
    extraContext = undefined,
    brandId = undefined,
    productIds = undefined,
    serviceIds = undefined,
} = {}) => {
    if (!projectIdea || !projectIdea.trim()) {
        throw new Error('projectIdea is required');
    }
    try {
        const requestBody = {
            projectIdea: projectIdea.trim(),
            count: Math.min(Math.max(count, 1), 6),
            genre,
            language,
        };
        // Only include model_key if we actually have one — otherwise let the
        // admin capability pick its default (Gemini 3 Pro per spec).
        // Feb 2026 — When the caller doesn't supply a modelKey, resolve
        // from AI Toolkit + tier catalog via the shared cascade. Always
        // append fallback_models[] so the backend has cascade coverage.
        const conceptCascade = buildCapabilityFallbackChain('concept_generation', [
            'concept_generation', 'Concept Generation', 'generate_concepts',
        ]);
        if (modelKey) {
            requestBody.model_key = modelKey;
        } else if (conceptCascade.modelKey) {
            requestBody.model_key = conceptCascade.modelKey;
        }
        if (conceptCascade.fallbackModels?.length) {
            requestBody.fallback_models = conceptCascade.fallbackModels;
        }
        // Optional product/brand image for vision-capable LLMs. Admin backend
        // ignores unknown fields safely; if it supports `reference_image`,
        // the model will actually study the image rather than treating the
        // filename as a text hint.
        if (referenceImage?.data) {
            requestBody.reference_image = {
                name: referenceImage.name,
                mime_type: referenceImage.mime_type,
                data: referenceImage.data,
            };
        }
        // Optional structured context (e.g. fetched URL references). Admin
        // backend ignores unknown fields safely; if it accepts `extra_context`,
        // the LLM uses scraped content as creative input.
        if (extraContext && typeof extraContext === 'object') {
            requestBody.extra_context = extraContext;
        }
        // Brand scope (Feb 2026): saved-brand id + selected product/service
        // ids. Backend fetches full context, filters to just these ids, and
        // prepends the brand block to the concept-generation prompt.
        if (brandId) requestBody.brand_id = brandId;
        if (Array.isArray(productIds) && productIds.length) requestBody.product_ids = productIds;
        if (Array.isArray(serviceIds) && serviceIds.length) requestBody.service_ids = serviceIds;

        console.log('🎯 generateConcepts request:', { projectIdeaLen: requestBody.projectIdea.length, count: requestBody.count, genre, language, modelKey, hasImage: !!referenceImage, hasExtraContext: !!extraContext, brandId, productIds, serviceIds });

        const submit = await apiCall('/ai/generate-concepts-async', {
            method: 'POST',
            body: JSON.stringify(requestBody),
        }, 30000);

        const jobId = submit?.job_id || submit?.result?.job_id;
        if (!jobId) {
            // Synchronous response path — admin returned result inline.
            const concepts = extractConcepts(submit);
            return { concepts, credits_used: submit?.credits_used || 0, model_used: submit?.model_used };
        }

        console.log('🔄 Concept generation job_id, polling:', jobId);
        const completed = await pollForJobResult(jobId, 60, 5000);
        const concepts = extractConcepts(completed);
        return {
            concepts,
            credits_used: completed?.credits_used || completed?.result?.credits_used || 0,
            model_used: completed?.model_used || completed?.result?.model_used,
        };
    } catch (error) {
        console.error('❌ Concept generation failed:', error);
        throw error;
    }
};

// Defensive parser — the admin backend returns `concepts` nested somewhere
// in the payload. Handle all common shapes so a schema tweak doesn't break us.
const extractConcepts = (payload) => {
    if (!payload) return [];
    // Try all likely locations in priority order.
    const candidates = [
        payload?.result?.result?.concepts,
        payload?.result?.concepts,
        payload?.concepts,
        payload?.data?.concepts,
    ];
    for (const c of candidates) {
        if (Array.isArray(c) && c.length > 0) {
            return c.map((it, i) => ({
                title: String(it?.title || it?.name || `Concept ${i + 1}`).trim(),
                hook: String(it?.hook || it?.opening || '').trim(),
                description: String(it?.description || it?.desc || it?.body || '').trim(),
            })).filter(it => it.title && (it.hook || it.description));
        }
    }
    // Last resort: look for a stringified JSON blob the admin may have nested.
    const rawText = payload?.result?.raw || payload?.result?.text || payload?.text;
    if (typeof rawText === 'string') {
        try {
            const parsed = JSON.parse(rawText);
            if (Array.isArray(parsed?.concepts)) return extractConcepts({ concepts: parsed.concepts });
            if (Array.isArray(parsed)) return extractConcepts({ concepts: parsed });
        } catch { /* noop */ }
    }
    console.warn('⚠️ No concepts found in payload:', payload);
    return [];
};


// ==================== URL REFERENCE FETCHING (Phase 1) ====================

/**
 * Fetch a public URL and return normalised reference content for LLM input.
 *
 * Endpoint: POST /api/ai/fetch-reference-async  →  GET /api/ai/jobs/{job_id}
 * Cost: 1 credit per successful fetch.
 *
 * Returns:
 *   {
 *     url, title, text, highlights[], images[], favicon, siteName,
 *     fetchedAt, creditsUsed
 *   }
 *
 * Throws Error with friendly message for known error codes:
 *   URL_BLOCKED, URL_TIMEOUT, URL_PAYWALL, URL_UNREACHABLE,
 *   FIRECRAWL_AUTH, FIRECRAWL_5XX, FETCH_ERROR.
 */
const URL_ERROR_MESSAGES = {
    URL_BLOCKED: "This URL can't be fetched (private or internal address).",
    URL_TIMEOUT: 'The page took too long to load. Try again, or use a different URL.',
    URL_PAYWALL: 'This site blocks scrapers. Try a different URL.',
    URL_UNREACHABLE: "Couldn't reach that URL — please check it's correct.",
    FIRECRAWL_AUTH: 'Reference service is misconfigured. Please contact support.',
    FIRECRAWL_5XX: 'Reference service is temporarily unavailable. Try again in a minute.',
    FETCH_ERROR: 'Fetch failed — please try again.',
};

const friendlyUrlError = (errString = '') => {
    const prefix = String(errString).split(':')[0]?.trim().toUpperCase();
    return URL_ERROR_MESSAGES[prefix] || (errString || URL_ERROR_MESSAGES.FETCH_ERROR);
};

export const fetchUrlReference = async ({
    url,
    maxImages = 6,
    maxTextChars = 12000,
    projectId = undefined,
    onProgress = undefined,
} = {}) => {
    if (!url || !/^https?:\/\//i.test(url.trim())) {
        throw new Error('Please enter a valid URL starting with http:// or https://');
    }

    const cleanUrl = url.trim();
    const userEmail = sessionStorage.getItem('userEmail') || undefined;

    const body = {
        url: cleanUrl,
        max_images: maxImages,
        max_text_chars: maxTextChars,
    };
    if (userEmail) body.user_email = userEmail;
    if (projectId) body.projectId = projectId;

    console.log('🔗 fetchUrlReference:', cleanUrl);

    if (onProgress) onProgress('Submitting URL to reference service…');

    let submitResp;
    try {
        submitResp = await apiCall('/ai/fetch-reference-async', {
            method: 'POST',
            body: JSON.stringify(body),
        }, 30000);
    } catch (e) {
        if (e.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Reference fetching costs 1 credit.');
        }
        throw e;
    }

    const jobId = submitResp.job_id || submitResp.result?.job_id;

    // Synchronous fallback path (admin returned result inline)
    if (!jobId) {
        const inline = submitResp.result || submitResp;
        return shapeReferenceResult(inline);
    }

    if (onProgress) onProgress('Fetching reference content…');

    // Poll up to ~75s (15 attempts × 5s). Most URLs complete in 3–25s.
    let completed;
    try {
        completed = await pollForJobResult(jobId, 15, 5000, (p) => {
            if (onProgress && typeof p === 'object') {
                const elapsed = (p.attempt || 0) * 5;
                onProgress(`Fetching… ${elapsed}s elapsed`);
            }
        });
    } catch (e) {
        if (e.isJobFailure) {
            throw new Error(friendlyUrlError(e.message));
        }
        throw e;
    }

    return shapeReferenceResult(completed?.result || completed, completed?.credits_used);
};

const shapeReferenceResult = (raw, fallbackCredits = 0) => {
    const r = raw || {};
    return {
        url: r.url || '',
        title: r.title || '',
        text: r.text || '',
        highlights: Array.isArray(r.highlights) ? r.highlights : [],
        images: Array.isArray(r.images) ? r.images : [],
        favicon: r.favicon || '',
        siteName: r.site_name || r.siteName || '',
        fetchedAt: r.fetched_at || r.fetchedAt || new Date().toISOString(),
        creditsUsed: r.credits_used || fallbackCredits || 0,
    };
};


// ==================== CO-PILOT CHAT (Phase 5, Feb 2026) ====================

/**
 * Ask the read-only Co-pilot a question. Server-side LLM call — the
 * Co-pilot is strictly informational, it never mutates the project.
 *
 * Endpoint: POST /api/ai/copilot-chat-async  →  GET /api/ai/jobs/{job_id}
 *
 * Request body:
 *   {
 *     question:  string,                  // user's chat message
 *     step:      string,                  // wizard step name ('script', 'concept', etc.)
 *     history?:  [{ role, content }, …],  // prior turns (last 6, optional)
 *     project?:  {                        // sanitized read-only snapshot
 *       name, duration, aspectRatio, tier, videoStyle, audioStyle,
 *       industryId, videoTypeId, brandName, characterCount, elementCount,
 *       scriptPreview, // first ~400 chars
 *     },
 *     user_email?: string,
 *   }
 *
 * Response (final result):
 *   { answer: string }   ← plain text, may include markdown
 *
 * Cost: 1 credit per successful chat turn (admin-configurable).
 */
export const requestCoPilotChat = async ({ question, step, history = [], project = {}, brandScope = {}, onProgress } = {}) => {
    if (!question || !question.trim()) {
        throw new Error('Please enter a question first.');
    }
    const userEmail = sessionStorage.getItem('userEmail') || undefined;

    const body = {
        question: question.trim(),
        step: step || 'unknown',
        history: Array.isArray(history) ? history.slice(-6) : [],
        project: project || {},
    };
    if (userEmail) body.user_email = userEmail;

    // Brand scope (Feb 2026): if the user has picked a saved brand on the
    // Story step, propagate the same brand_id + product_ids + service_ids to
    // the co-pilot so its answers reference the exact products the user is
    // building an ad for.
    if (brandScope?.brandId) body.brand_id = brandScope.brandId;
    if (Array.isArray(brandScope?.productIds) && brandScope.productIds.length) body.product_ids = brandScope.productIds;
    if (Array.isArray(brandScope?.serviceIds) && brandScope.serviceIds.length) body.service_ids = brandScope.serviceIds;

    if (onProgress) onProgress('Asking the Co-pilot…');

    let submitResp;
    try {
        submitResp = await apiCall('/ai/copilot-chat-async', {
            method: 'POST',
            body: JSON.stringify(body),
        }, 30000);
    } catch (e) {
        if (e.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Co-pilot chat costs 1 credit.');
        }
        throw e;
    }

    const jobId = submitResp.job_id || submitResp.result?.job_id;

    // Synchronous fallback path (admin returned result inline)
    if (!jobId) {
        const inline = submitResp.result || submitResp;
        return {
            answer:       inline?.answer || inline?.text || '',
            actions:      Array.isArray(inline?.actions) ? inline.actions : [],
            creditsUsed:  inline?.credits_used || 0,
        };
    }

    if (onProgress) onProgress('Thinking…');

    // Poll up to ~45s. LLM answers complete in 3–25s.
    const completed = await pollForJobResult(jobId, 15, 3000, (p) => {
        if (onProgress && typeof p === 'object') {
            const elapsed = (p.attempt || 0) * 3;
            onProgress(`Thinking… ${elapsed}s elapsed`);
        }
    });

    const r = completed?.result || completed || {};
    return {
        answer: r.answer || r.text || '',
        // Phase 6 (Feb 2026): backend may return an `actions` array of
        // proposed scoped mutations (validated against the allow-list in
        // `coPilotActions.js` before they're rendered).
        actions: Array.isArray(r.actions) ? r.actions : [],
        creditsUsed: r.credits_used || completed?.credits_used || 0,
    };
};



// ==================== SOCIAL ACCOUNT FETCHING (Phase 1.5) ====================

/**
 * Fetch recent posts from a public social account (YouTube / TikTok / Instagram).
 *
 * Endpoint: POST /api/ai/fetch-social-account-async  →  GET /api/ai/jobs/{job_id}
 * Cost: 10 credits per successful fetch.
 *
 * Two input modes — pass EITHER (platform + handle) OR url:
 *   fetchSocialAccount({ platform: 'youtube', handle: '@MKBHD', limit: 12 })
 *   fetchSocialAccount({ url: 'https://instagram.com/glossier', limit: 12 })
 *
 * Returns:
 *   {
 *     platform, handle,
 *     profile: { url, displayName, avatar, bio, followers, verified },
 *     items:   [{ id, postUrl, title, text, highlights, images, videoUrl,
 *                 thumbnail, mediaType, postedAt, engagement, durationSeconds }],
 *     itemsCount, fetchedAt, creditsUsed,
 *   }
 *
 * Latency: YouTube ~2-5s, TikTok/Instagram ~15-90s (Apify spin-up).
 */

const SOCIAL_ERROR_MESSAGES = {
    PLATFORM_UNSUPPORTED: "We don't support that platform yet. Try YouTube, TikTok, or Instagram.",
    URL_INVALID_FOR_PLATFORM: "This URL isn't recognised. Use a YouTube channel, TikTok handle, or Instagram profile URL.",
    HANDLE_NOT_FOUND: "We couldn't find that account. It may not exist, or it could be private.",
    YOUTUBE_QUOTA_EXCEEDED: 'YouTube fetching is at capacity for today. Try again tomorrow, or use a different platform.',
    YOUTUBE_AUTH: 'Reference service is misconfigured. Please contact support.',
    APIFY_AUTH: 'Reference service is misconfigured. Please contact support.',
    APIFY_ERROR: 'Reference service hit a snag — please try again in a minute.',
    FETCH_ERROR: 'Fetch failed — please try again.',
};

const friendlySocialError = (errString = '') => {
    const prefix = String(errString).split(':')[0]?.trim().toUpperCase();
    return SOCIAL_ERROR_MESSAGES[prefix] || (errString || SOCIAL_ERROR_MESSAGES.FETCH_ERROR);
};

export const fetchSocialAccount = async ({
    platform,
    handle,
    url,
    limit = 12,
    projectId = undefined,
    onProgress = undefined,
} = {}) => {
    const hasPair = !!(platform && handle);
    const hasUrl = !!url;
    if (!hasPair && !hasUrl) {
        throw new Error('Provide either (platform + handle) or a URL.');
    }
    if (hasUrl && !/^https?:\/\//i.test(url.trim())) {
        throw new Error('Please enter a valid URL starting with http:// or https://');
    }

    const userEmail = sessionStorage.getItem('userEmail') || undefined;
    const body = { limit: Math.max(1, Math.min(limit, 50)) };
    if (hasPair) {
        body.platform = String(platform).toLowerCase().trim();
        body.handle = String(handle).trim();
    } else {
        body.url = url.trim();
    }
    if (userEmail) body.user_email = userEmail;
    if (projectId) body.projectId = projectId;

    console.log('🔗 fetchSocialAccount:', body);
    if (onProgress) onProgress('Submitting to social fetch service…');

    let submitResp;
    try {
        submitResp = await apiCall('/ai/fetch-social-account-async', {
            method: 'POST',
            body: JSON.stringify(body),
        }, 30000);
    } catch (e) {
        if (e.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Social account fetching costs 10 credits.');
        }
        throw e;
    }

    const jobId = submitResp.job_id || submitResp.result?.job_id;
    if (!jobId) {
        return shapeSocialResult(submitResp.result || submitResp);
    }

    if (onProgress) onProgress('Fetching recent posts… (this can take up to ~90 seconds for TikTok / Instagram)');

    // Apify-backed fetches can take 60-90s. Poll up to ~150s (30 attempts × 5s).
    let completed;
    try {
        completed = await pollForJobResult(jobId, 30, 5000, (p) => {
            if (onProgress && typeof p === 'object') {
                const elapsed = (p.attempt || 0) * 5;
                onProgress(`Fetching… ${elapsed}s elapsed`);
            }
        });
    } catch (e) {
        if (e.isJobFailure) {
            throw new Error(friendlySocialError(e.message));
        }
        throw e;
    }

    return shapeSocialResult(completed?.result || completed, completed?.credits_used);
};

const shapeSocialResult = (raw, fallbackCredits = 0) => {
    const r = raw || {};
    const profileSrc = r.profile || {};
    const itemsSrc = Array.isArray(r.items) ? r.items : [];
    return {
        platform: r.platform || '',
        handle: r.handle || '',
        profile: {
            url: profileSrc.url || '',
            displayName: profileSrc.display_name || profileSrc.displayName || '',
            avatar: profileSrc.avatar || '',
            bio: profileSrc.bio || '',
            followers: profileSrc.followers ?? null,
            verified: !!profileSrc.verified,
        },
        items: itemsSrc.map(i => ({
            id: i.id || '',
            postUrl: i.post_url || i.postUrl || '',
            title: i.title || '',
            text: i.text || '',
            highlights: Array.isArray(i.highlights) ? i.highlights : [],
            images: Array.isArray(i.images) ? i.images : [],
            videoUrl: i.video_url || i.videoUrl || null,
            thumbnail: i.thumbnail || null,
            mediaType: i.media_type || i.mediaType || 'image',
            postedAt: i.posted_at || i.postedAt || null,
            engagement: i.engagement || { likes: null, comments: null, views: null, shares: null },
            durationSeconds: i.duration_seconds ?? i.durationSeconds ?? null,
        })),
        itemsCount: r.items_count ?? itemsSrc.length,
        fetchedAt: r.fetched_at || r.fetchedAt || new Date().toISOString(),
        creditsUsed: r.credits_used || fallbackCredits || 0,
    };
};

/**
 * Detect platform from a URL or bare handle. Returns null for bare handles
 * (caller should show a platform picker).
 */
export const detectSocialPlatform = (input) => {
    const v = (input || '').trim();
    if (!v) return null;
    if (/^https?:\/\//i.test(v)) {
        if (/youtube\.com|youtu\.be/i.test(v)) return 'youtube';
        if (/tiktok\.com/i.test(v)) return 'tiktok';
        if (/instagram\.com/i.test(v)) return 'instagram';
        return null;
    }
    return null; // bare handle — let user pick
};


// ==================== VIDEO ANALYSIS (Phase 2) ====================

/**
 * Phase 2: Video Understanding via Gemini.
 *
 * Three flows:
 *   1. YouTube / direct-mp4 URL  →  analyzeVideo({ videoUrl })
 *   2. File upload (≤100 MB)     →  getVideoUploadUrl()  →  uploadVideoToGcs()
 *                                    →  analyzeVideo({ gcsPath })
 *
 * Cost: 50 credits per successful analysis.
 * Limits: 100 MB max file, 30 min max duration, 15 min upload-URL TTL.
 */

const VIDEO_ERROR_MESSAGES = {
    VIDEO_URL_UNSUPPORTED: "We can't analyse this URL — paste a YouTube link or upload the file directly.",
    VIDEO_TYPE_UNSUPPORTED: 'Only video files are supported (MP4, WebM, MOV, MKV, M4V).',
    VIDEO_TOO_LARGE: 'Video is too large. Maximum file size is 100 MB.',
    VIDEO_INVALID_SIZE: 'Video file looks empty.',
    GCS_OBJECT_NOT_FOUND: 'Upload expired. Please re-upload the file.',
    GCS_AUTH: 'Reference service is misconfigured. Please contact support.',
    GEMINI_QUOTA: 'Service is busy. Please try again in a few minutes.',
    GEMINI_BLOCKED: "We can't analyse this video — content was flagged by the safety filter.",
    GEMINI_PARSE_FAILED: 'Analysis returned an unexpected response. Please retry.',
    ANALYSIS_ERROR: 'Video analysis failed. Please try again.',
};

const friendlyVideoError = (errString = '') => {
    const prefix = String(errString).split(':')[0]?.trim().toUpperCase();
    return VIDEO_ERROR_MESSAGES[prefix] || (errString || VIDEO_ERROR_MESSAGES.ANALYSIS_ERROR);
};

/**
 * Step 1 of the upload flow — get a signed GCS PUT URL.
 */
export const getVideoUploadUrl = async ({ filename, contentType, sizeBytes, projectId } = {}) => {
    if (!filename || !contentType || !sizeBytes) {
        throw new Error('filename, contentType, and sizeBytes are required.');
    }
    if (!contentType.startsWith('video/')) {
        throw new Error(VIDEO_ERROR_MESSAGES.VIDEO_TYPE_UNSUPPORTED);
    }

    const userEmail = sessionStorage.getItem('userEmail') || undefined;
    const body = { filename, content_type: contentType, size_bytes: sizeBytes };
    if (userEmail) body.user_email = userEmail;
    if (projectId) body.projectId = projectId;

    try {
        const resp = await apiCall('/ai/get-video-upload-url', {
            method: 'POST',
            body: JSON.stringify(body),
        }, 30000);
        return {
            uploadUrl: resp.upload_url,
            gcsPath: resp.gcs_path,
            publicUrl: resp.public_url,
            expiresAt: resp.expires_at,
            maxSizeBytes: resp.max_size_bytes,
            requiredHeaders: resp.required_headers || {},
        };
    } catch (e) {
        throw new Error(friendlyVideoError(e.message));
    }
};

/**
 * Step 2 of the upload flow — PUT the file body directly to GCS using
 * XMLHttpRequest so we can surface real upload progress.
 */
export const uploadVideoToGcs = ({ uploadUrl, requiredHeaders = {}, file, onProgress } = {}) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        Object.entries(requiredHeaders).forEach(([k, v]) => xhr.setRequestHeader(k, v));

        if (onProgress && xhr.upload) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress({ loaded: e.loaded, total: e.total, percent: Math.round((e.loaded / e.total) * 100) });
                }
            };
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve({ success: true });
            else reject(new Error(`Upload failed (${xhr.status}). Please try again.`));
        };
        xhr.onerror = () => reject(new Error('Upload network error. Please check your connection.'));
        xhr.onabort = () => reject(new Error('Upload cancelled.'));
        xhr.send(file);
    });
};

/**
 * Step 3 (or single step for YouTube/direct URLs) — submit the analyse-video
 * job and poll until done.
 */
export const analyzeVideo = async ({
    videoUrl,
    gcsPath,
    projectId = undefined,
    onProgress = undefined,
} = {}) => {
    if (!videoUrl && !gcsPath) {
        throw new Error('Provide either videoUrl or gcsPath.');
    }
    if (videoUrl && gcsPath) {
        throw new Error('Provide only one of videoUrl or gcsPath.');
    }

    const userEmail = sessionStorage.getItem('userEmail') || undefined;
    const body = {};
    if (videoUrl) body.video_url = videoUrl.trim();
    if (gcsPath) body.gcs_path = gcsPath;
    if (userEmail) body.user_email = userEmail;
    if (projectId) body.projectId = projectId;

    // Feb 2026 — Capability-based model + cascading fallback for the
    // video-understanding LLM.
    const videoUnderstandingCascade = buildCapabilityFallbackChain('video_understanding', [
        'video_understanding', 'Video Understanding', 'analyze_video',
    ]);
    if (videoUnderstandingCascade.modelKey)                     body.model_key       = videoUnderstandingCascade.modelKey;
    if (videoUnderstandingCascade.fallbackModels?.length)       body.fallback_models = videoUnderstandingCascade.fallbackModels;

    if (onProgress) onProgress('Submitting video for analysis…');

    let submitResp;
    try {
        submitResp = await apiCall('/ai/analyze-video-async', {
            method: 'POST',
            body: JSON.stringify(body),
        }, 30000);
    } catch (e) {
        if (e.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Video analysis costs 50 credits.');
        }
        throw new Error(friendlyVideoError(e.message));
    }

    const jobId = submitResp.job_id || submitResp.result?.job_id;
    if (!jobId) {
        return shapeVideoResult(submitResp.result || submitResp);
    }

    if (onProgress) onProgress('Watching the video… (this can take 30-90 seconds)');

    // Poll up to ~5 minutes for `deep` jobs / large files. 60 attempts × 5s = 300s.
    let completed;
    try {
        completed = await pollForJobResult(jobId, 60, 5000, (p) => {
            if (onProgress && typeof p === 'object') {
                const elapsed = (p.attempt || 0) * 5;
                onProgress(`Analysing… ${elapsed}s elapsed`);
            }
        });
    } catch (e) {
        if (e.isJobFailure) {
            throw new Error(friendlyVideoError(e.message));
        }
        throw e;
    }

    return shapeVideoResult(completed?.result || completed, completed?.credits_used);
};

const shapeVideoResult = (raw, fallbackCredits = 0) => {
    const r = raw || {};
    return {
        sourceUrl: r.source_url || r.sourceUrl || '',
        sourceType: r.source_type || r.sourceType || '',
        title: r.title || '',
        text: r.text || '',
        highlights: Array.isArray(r.highlights) ? r.highlights : [],
        images: Array.isArray(r.images) ? r.images : [],
        favicon: r.favicon || '',
        siteName: r.site_name || r.siteName || '',
        durationSeconds: r.duration_seconds ?? r.durationSeconds ?? 0,
        thumbnail: r.thumbnail || (Array.isArray(r.images) && r.images[0]) || '',
        segments: Array.isArray(r.segments) ? r.segments : [],
        transcript: r.transcript || { language: '', text: '' },
        visualStyle: r.visual_style || r.visualStyle || {},
        audioStyle: r.audio_style || r.audioStyle || {},
        fetchedAt: r.fetched_at || r.fetchedAt || new Date().toISOString(),
        creditsUsed: r.credits_used || fallbackCredits || 0,
    };
};




/**
 * Parse characters and elements from markdown/text format
 * Handles Gemini's text-based descriptions for both characters and elements
 */
const parseCharactersFromText = (text) => {
    const items = [];
    
    console.log('🔍 Parsing text, length:', text?.length, 'Preview:', text?.substring(0, 300));
    
    if (!text || typeof text !== 'string') {
        console.warn('⚠️ No valid text to parse');
        return items;
    }
    
    /**
     * Helper function to determine the correct category for an item
     * @param {Object} item - The item with name and description
     * @returns {string} The correct category: 'character', 'narrator', or 'element'
     */
    const determineCategory = (item) => {
        const lowerName = (item.name || '').toLowerCase();
        const lowerDescription = (item.description || '').toLowerCase();
        
        // Body parts should be filtered out completely - return null to signal skip
        const bodyPartPatterns = [
            /\b(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|nose|mouth|lips|teeth|hair|head|face|neck|shoulder|shoulders|chest|back|stomach|knee|knees|elbow|elbows|wrist|wrists|ankle|ankles|thumb|thumbs|toe|toes|skin|nail|nails|palm|palms|forehead|chin|cheek|cheeks|eyebrow|eyebrows|eyelash|eyelashes|beard|mustache|torso|hip|hips|thigh|thighs|calf|calves)\b/i,
            /\b(\w+)'s\s+(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|face|hair|head|body|skin)\b/i
        ];
        
        if (bodyPartPatterns.some(pattern => pattern.test(lowerName))) {
            return null; // Signal to skip this item
        }
        
        // Check for narrator - flexible matching
        const narratorNamePatterns = [
            /^narrator\b/i,                    // Starts with "Narrator"
            /\bnarrator\b/i,                   // Contains "narrator" as a word
            /^voice\s*over\b/i,                // Starts with "Voice Over" or "Voiceover"
            /^vo\b/i,                          // Starts with "VO"
            /\bstoryteller\b/i                 // Contains "storyteller"
        ];
        
        const narratorDescPatterns = [
            /\bnarrator\b/i,
            /\bomniscient\b/i,
            /\bvoice\s*over\b/i,
            /\bnarration\b/i,
            /\bstoryteller\b/i,
            /\bnarrates\b/i
        ];
        
        const isNarratorByName = narratorNamePatterns.some(pattern => pattern.test(lowerName));
        const isNarratorByDesc = narratorDescPatterns.some(pattern => pattern.test(lowerDescription));
        
        if (isNarratorByName || isNarratorByDesc) {
            console.log('🎙️ Detected narrator:', item.name, { byName: isNarratorByName, byDesc: isNarratorByDesc });
            return 'narrator';
        }
        
        // Check for elements/props/locations
        const elementPatterns = /\b(sink|cloth|lamp|shrine|idol|bell|mist|fog|rain|temple|stone|flame|moss|camera|shot|angle|light|sound|music|building|car|vehicle|tree|forest|city|room|house|office|door|window|table|chair|desk|phone|computer|book|letter|key|mirror|clock|painting|photograph|ring|necklace|sword|gun|knife)\b/i;
        
        if (elementPatterns.test(lowerName)) {
            return 'element';
        }
        
        // Default to keeping the original category or 'character'
        return item.category || 'character';
    };
    
    // First, try to parse as JSON
    try {
        const jsonData = JSON.parse(text);
        if (jsonData.characters && Array.isArray(jsonData.characters)) {
            console.log('✅ Parsed as JSON, found characters array with', jsonData.characters.length, 'items');
            // Apply category correction to each item - ALWAYS recategorize
            return jsonData.characters
                .map(char => {
                    const correctCategory = determineCategory(char);
                    if (correctCategory === null) {
                        console.log('⏭️ Skipping body part from JSON:', char.name);
                        return null;
                    }
                    // Log if we're changing the category
                    if (char.category && char.category !== correctCategory) {
                        console.log(`🔄 Recategorizing "${char.name}" from "${char.category}" to "${correctCategory}"`);
                    }
                    return {
                        ...char,
                        category: correctCategory
                    };
                })
                .filter(Boolean); // Remove null items (body parts)
        } else if (jsonData.result?.characters && Array.isArray(jsonData.result.characters)) {
            console.log('✅ Parsed as JSON, found result.characters array with', jsonData.result.characters.length, 'items');
            return jsonData.result.characters
                .map(char => {
                    const correctCategory = determineCategory(char);
                    if (correctCategory === null) {
                        console.log('⏭️ Skipping body part from JSON:', char.name);
                        return null;
                    }
                    // Log if we're changing the category
                    if (char.category && char.category !== correctCategory) {
                        console.log(`🔄 Recategorizing "${char.name}" from "${char.category}" to "${correctCategory}"`);
                    }
                    return {
                        ...char,
                        category: correctCategory
                    };
                })
                .filter(Boolean);
        }
    } catch (e) {
        console.log('🔄 Not JSON format, continuing with markdown parsing...');
    }
    
    // Continue with markdown parsing if not JSON
    // Look for CHARACTER sections in the script with various formats
    
    // First, try to find ## CHARACTER sections (format from backend)
    const characterSections = text.match(/##\s*CHARACTER\s*\d*[:\s]+([^\n]+)[\s\S]*?(?=##\s*(?:CHARACTER|NARRATOR|KEY\s*ELEMENT|SCENE|$))/gi);
    const narratorSections = text.match(/##\s*NARRATOR[:\s]*([^\n]*)[\s\S]*?(?=##\s*(?:CHARACTER|KEY\s*ELEMENT|SCENE|$))/gi);
    const elementSections = text.match(/##\s*KEY\s*ELEMENT[S]?[:\s]*[\s\S]*?(?=##\s*(?:CHARACTER|NARRATOR|SCENE|$))/gi);
    
    console.log('📋 Found sections:', {
        characters: characterSections?.length || 0,
        narrators: narratorSections?.length || 0,
        elements: elementSections?.length || 0
    });
    
    // Process CHARACTER sections
    if (characterSections) {
        for (const section of characterSections) {
            console.log('📄 Processing CHARACTER section:', section.substring(0, 150));
            
            // Extract name from header like "## CHARACTER 1: AYESHA"
            const headerMatch = section.match(/##\s*CHARACTER\s*\d*[:\s]+([^\n]+)/i);
            if (!headerMatch) continue;
            
            const name = headerMatch[1].trim();
            console.log('✓ Found character:', name);
            
            // Extract description from **Physical Description:** field
            const physDescMatch = section.match(/\*\*Physical\s*Description[:\s]*\*\*\s*([^\n]+(?:\n(?!\*\*)[^\n]+)*)/i);
            let description = physDescMatch ? physDescMatch[1].trim() : '';
            
            // Also try **Description:** format
            if (!description) {
                const descMatch = section.match(/\*\*Description[:\s]*\*\*\s*([^\n]+(?:\n(?!\*\*)[^\n]+)*)/i);
                description = descMatch ? descMatch[1].trim() : '';
            }
            
            // Fallback to first paragraph after header
            if (!description) {
                const lines = section.split('\n').slice(1).filter(l => l.trim() && !l.startsWith('##'));
                description = lines.slice(0, 3).join(' ').replace(/\*\*/g, '').trim();
            }
            
            const category = determineCategory({ name, description, category: 'character' });
            if (category === null) continue;
            
            items.push({
                name,
                description: description || `${name} - character from the scene`,
                category
            });
            console.log('✅ Added character:', { name, category, descLength: description.length });
        }
    }
    
    // Process NARRATOR sections
    if (narratorSections) {
        for (const section of narratorSections) {
            console.log('📄 Processing NARRATOR section:', section.substring(0, 150));
            
            // Extract narrator info
            const voiceMatch = section.match(/\*\*Voice\s*(?:Style|Description)[:\s]*\*\*\s*([^\n]+)/i);
            const description = voiceMatch ? voiceMatch[1].trim() : 'Narrator voice for the scene';
            
            items.push({
                name: 'Narrator',
                description,
                category: 'narrator'
            });
            console.log('✅ Added narrator');
        }
    }
    
    // Process KEY ELEMENTS sections
    if (elementSections) {
        for (const section of elementSections) {
            console.log('📄 Processing ELEMENTS section:', section.substring(0, 200));
            
            // Find individual elements - look for bullet points or numbered items
            const elementMatches = section.match(/(?:^|\n)\s*[-*•]\s*\*\*([^*]+)\*\*[:\s]*([^\n]+)/gm);
            
            if (elementMatches) {
                for (const match of elementMatches) {
                    const itemMatch = match.match(/[-*•]\s*\*\*([^*]+)\*\*[:\s]*(.+)/);
                    if (itemMatch) {
                        const name = itemMatch[1].trim();
                        const description = itemMatch[2].trim();
                        
                        const category = determineCategory({ name, description, category: 'element' });
                        if (category === null) continue;
                        
                        items.push({ name, description, category });
                        console.log('✅ Added element:', { name, category });
                    }
                }
            }
        }
    }
    
    // If no items found with structured parsing, try fallback methods
    if (items.length === 0) {
        console.log('🔄 No items from structured parsing, trying fallback patterns...');
        
        // Look for different section patterns - MORE FLEXIBLE matching
        // Split by double newlines or bold headers
        const sections = text.split(/\n\n+|\*\*(?:Character|Element|Prop|Location|Object|Item)[^*]*\*\*/gi);
        
        console.log('📋 Split into sections:', sections.length);
        
        // Also try splitting by numbered lists or bullet points
        const altSections = text.match(/(?:^|\n)(?:\d+\.|[-*])\s*\*\*[^*]+\*\*[\s\S]*?(?=(?:\n(?:\d+\.|[-*])\s*\*\*)|$)/g);
        
        if (altSections && altSections.length > sections.length) {
            console.log('📋 Using numbered list format, found:', altSections.length, 'items');
            sections.length = 0;
            sections.push(...altSections);
        }
        
        for (const section of sections) {
            if (!section.trim() || section.length < 10) continue;
            
            console.log('📄 Processing fallback section:', section.substring(0, 100));
            
            // Extract name from bold headers - MORE PATTERNS
            const nameMatch = section.match(/\*\*(?:Character Profile:|Character:|Element:|Prop:|Location:|Object:|Item:)?\s*([^*\n]+)\*\*/i);
            if (!nameMatch) {
                console.log('⚠️ No name found in section');
                continue;
            }
            
            const name = nameMatch[1].trim().replace(/["']/g, '');
            const content = section.substring(nameMatch[0].length).trim();
            
            console.log('✓ Found item:', name);
            
            // Extract description - try multiple patterns
            let description = '';
            
            // Look for Physical Description or Description field
            const descMatch = content.match(/\*\*(?:Physical Description|Description|Details|Appearance):\*\*\s*([^\*\n]+)/i);
            if (descMatch) {
                description = descMatch[1].trim();
            } else {
                // Use the first substantial paragraph
                const paragraphs = content.split('\n\n').filter(p => p.trim() && !p.match(/^\*\*\w+:\*\*/));
                if (paragraphs.length > 0) {
                    description = paragraphs[0].replace(/\*\*/g, '').trim();
                } else {
                    // Use first few lines
                    const lines = content.split('\n').filter(l => l.trim() && !l.match(/^\*\*\w+:\*\*/));
                    description = lines.slice(0, 3).join(' ').replace(/\*\*/g, '').trim();
                }
            }
            
            // If still no description, use the whole content
            if (!description || description.length < 10) {
                description = content.replace(/\*\*/g, '').replace(/\n+/g, ' ').trim().substring(0, 200);
            }
            
            // Use the determineCategory helper to get the correct category
            const itemForCategorization = { name, description, category: 'character' };
            
            // Check section header for additional context
            const lowerSection = section.toLowerCase();
            if (lowerSection.includes('**element') || 
                lowerSection.includes('**prop') || 
                lowerSection.includes('**location') || 
                lowerSection.includes('**object') ||
                lowerSection.includes('**setting') ||
                lowerSection.includes('**item') ||
                lowerSection.includes('**other element')) {
                itemForCategorization.category = 'element';
            } else if (lowerSection.includes('**narrator') || lowerSection.includes('narrator:')) {
                itemForCategorization.category = 'narrator';
            }
            
            const category = determineCategory(itemForCategorization);
            
            // Skip if determineCategory returns null (body parts)
            if (category === null) {
                console.log('⏭️ Skipping body part from markdown:', name);
                continue;
            }
            
            // Extract additional metadata if available
            const ageMatch = content.match(/\*\*Age:\*\*\s*(\d+)/i);
            const roleMatch = content.match(/\*\*Role:\*\*\s*([^\n*]+)/i);
            
            const item = {
                name: name,
                description: description || `${name} from the scene`,
                category: category
            };
            
            if (roleMatch) item.role = roleMatch[1].trim();
            if (ageMatch) item.age = parseInt(ageMatch[1]);
            
            items.push(item);
            console.log('✅ Added item:', { name, category, descLength: description.length });
        }
    }
    
    console.log(`📝 Parsed ${items.length} items from text (${items.filter(i => i.category === 'character').length} characters, ${items.filter(i => i.category === 'element').length} elements, ${items.filter(i => i.category === 'narrator').length} narrators)`);
    return items;
};

/**
 * Generate characters from script
 *
 * Async migration (Feb 13, 2026):
 *   • Submits to `/ai/generate-characters-async` and polls `/ai/jobs/{job_id}`
 *     every 3s for up to 100 attempts (~5 min). This eliminates the 30s/120s
 *     proxy timeout that previously broke generation for long scripts.
 *   • `completed.result` from the polling endpoint is byte-identical to the
 *     payload the old sync endpoint returned at `response.data.result`, so
 *     the downstream parser below is unchanged — we just wrap the polled
 *     result back into a `{ result, credits_used }` envelope to match the
 *     pre-existing shape consumed by the parser.
 *
 * @param {string} script - Script text to extract characters from
 * @param {Object} [options] - Optional generation knobs
 * @param {string} [options.style] - Visual style hint (e.g., "realistic")
 * @param {number} [options.num_characters] - Target character count
 * @param {string} [options.model_key] - Override model selection
 * @param {Function} [options.onProgress] - Polling progress callback `({ attempt, status })`
 */
export const generateCharacters = async (script, options = {}) => {
    try {
        const { style, num_characters, model_key, onProgress } = options;
        const submitBody = { script };
        if (style) submitBody.style = style;
        if (num_characters) submitBody.num_characters = num_characters;

        // Feb 2026 — Capability-based model + cascading fallback for the
        // character-generation reasoning LLM.
        const charCascade = buildCapabilityFallbackChain('character_generation', [
            'character_generation', 'Character Generation', 'generate_characters',
        ]);
        if (model_key) {
            submitBody.model_key = model_key;
        } else if (charCascade.modelKey) {
            submitBody.model_key = charCascade.modelKey;
        }
        if (charCascade.fallbackModels?.length) {
            submitBody.fallback_models = charCascade.fallbackModels;
        }

        // 1) Submit — async endpoint returns a job_id within ~50ms.
        console.log('🎭 Submitting async character generation job...');
        const submit = await apiCall('/ai/generate-characters-async', {
            method: 'POST',
            body: JSON.stringify(submitBody)
        }, 30000);

        const jobId = submit?.job_id || submit?.result?.job_id;
        if (!jobId) {
            // Defensive: if the backend still returned an immediate result
            // (e.g. fallback path during rollout), fall through to the same
            // parser that the old sync endpoint fed.
            console.warn('⚠️ Async submit did not return a job_id — treating response as immediate result');
            return await parseGenerateCharactersResponse(submit, script);
        }

        // 2) Poll every 3s, up to 100 attempts (~5 min). Spec-mandated cadence.
        console.log(`🔄 Polling character generation job ${jobId}...`);
        const completed = await pollForJobResult(jobId, 100, 3000, (p) => {
            if (typeof p === 'object' && onProgress) onProgress(p);
        });

        // 3) Re-wrap the polled result into the envelope shape the existing
        //    parser expects (`result.result`, `result.credits_used`). The
        //    polled `completed.result` is byte-identical to the old sync
        //    endpoint's `response.data.result`.
        const wrapped = {
            result: completed?.result ?? completed,
            credits_used: completed?.credits_used || completed?.result?.credits_used || 0
        };

        return await parseGenerateCharactersResponse(wrapped, script);
    } catch (error) {
        console.error('❌ Character generation failed:', error);
        throw error;
    }
};

/**
 * Parse a `generate-characters` response into the
 * `{ characters, otherElements, narrators, allItems, credits_used }` shape
 * consumed by App.js. Extracted from the original sync `generateCharacters`
 * body so both the async polled result AND the (rare) immediate-result path
 * share one parser.
 */
const parseGenerateCharactersResponse = async (result, script) => {
    try {
        console.log('🔍 FULL Backend Response:', JSON.stringify(result, null, 2));
        
        // Try to extract string content from various possible locations
        let textContent = null;
        let allItems = null;
        
        // Check for text/string responses that need parsing
        if (typeof result?.result === 'string') {
            textContent = result.result;
        } else if (typeof result?.result?.result === 'string') {
            textContent = result.result.result;
        } else if (typeof result?.result?.text === 'string') {
            textContent = result.result.text;
        } else if (typeof result?.text === 'string') {
            textContent = result.text;
        }
        
        // If we found text content, parse it
        if (textContent) {
            console.log('🔄 Found text content, parsing markdown...');
            console.log('📝 Text preview:', textContent.substring(0, 500));
            allItems = parseCharactersFromText(textContent);
        } else {
            // Try to get array data from various locations
            allItems = result?.result?.items || 
                      result?.items ||
                      result?.result?.characters ||
                      result?.characters ||
                      result?.result?.data ||
                      result?.data ||
                      result?.result ||
                      result;
        }
        
        console.log('✅ Character generation raw response:', {
            hasResult: !!result,
            resultKeys: result ? Object.keys(result) : [],
            resultDotResult: result?.result ? Object.keys(result.result) : [],
            foundTextContent: !!textContent,
            responseType: typeof allItems,
            isArray: Array.isArray(allItems),
            isString: typeof allItems === 'string',
            itemsLength: Array.isArray(allItems) ? allItems.length : 'N/A',
            preview: typeof allItems === 'string' ? allItems.substring(0, 500) : (Array.isArray(allItems) ? `Array with ${allItems.length} items` : JSON.stringify(allItems).substring(0, 500))
        });
        
        // Handle string response (Gemini returns markdown/text)
        if (typeof allItems === 'string') {
            console.log('🔄 Parsing markdown character descriptions...');
            allItems = parseCharactersFromText(allItems);
        }
        
        // If still an object (not an array), try to parse it as text
        if (!Array.isArray(allItems) && typeof allItems === 'object' && allItems !== null) {
            console.warn('⚠️ Got object instead of array, checking for text fields...');
            
            // First, try to stringify and parse as JSON (backend might return nested JSON string)
            const objString = JSON.stringify(allItems);
            
            // Try to parse as JSON first
            try {
                const parsed = JSON.parse(objString);
                
                // Backend may return { characters: [], otherElements: [], narrators: [] }
                // We need to combine all of them
                let combinedItems = [];
                
                if (parsed.characters && Array.isArray(parsed.characters)) {
                    console.log(`✅ Found ${parsed.characters.length} characters in response`);
                    combinedItems = [...combinedItems, ...parsed.characters.map(c => ({ ...c, category: c.category || 'character' }))];
                }
                if (parsed.otherElements && Array.isArray(parsed.otherElements)) {
                    console.log(`✅ Found ${parsed.otherElements.length} otherElements in response`);
                    combinedItems = [...combinedItems, ...parsed.otherElements.map(e => ({ ...e, category: e.category || 'element' }))];
                }
                if (parsed.narrators && Array.isArray(parsed.narrators)) {
                    console.log(`✅ Found ${parsed.narrators.length} narrators in response`);
                    combinedItems = [...combinedItems, ...parsed.narrators.map(n => ({ ...n, category: n.category || 'narrator' }))];
                }
                if (parsed.elements && Array.isArray(parsed.elements)) {
                    console.log(`✅ Found ${parsed.elements.length} elements in response`);
                    combinedItems = [...combinedItems, ...parsed.elements.map(e => ({ ...e, category: e.category || 'element' }))];
                }
                
                // Also check nested result object
                if (parsed.result) {
                    if (parsed.result.characters && Array.isArray(parsed.result.characters)) {
                        console.log(`✅ Found ${parsed.result.characters.length} characters in result`);
                        combinedItems = [...combinedItems, ...parsed.result.characters.map(c => ({ ...c, category: c.category || 'character' }))];
                    }
                    if (parsed.result.otherElements && Array.isArray(parsed.result.otherElements)) {
                        console.log(`✅ Found ${parsed.result.otherElements.length} otherElements in result`);
                        combinedItems = [...combinedItems, ...parsed.result.otherElements.map(e => ({ ...e, category: e.category || 'element' }))];
                    }
                    if (parsed.result.narrators && Array.isArray(parsed.result.narrators)) {
                        console.log(`✅ Found ${parsed.result.narrators.length} narrators in result`);
                        combinedItems = [...combinedItems, ...parsed.result.narrators.map(n => ({ ...n, category: n.category || 'narrator' }))];
                    }
                }
                
                if (combinedItems.length > 0) {
                    console.log(`✅ Combined ${combinedItems.length} total items from backend response`);
                    allItems = combinedItems;
                } else {
                    // If object contains character data as text, parse as markdown
                    if (objString.includes('character') || objString.includes('Character') || objString.includes('CHARACTER')) {
                        console.log('🔄 Object contains character text data, attempting to parse as markdown...');
                        allItems = parseCharactersFromText(objString);
                    } else {
                        console.warn('⚠️ Object does not contain parseable character data');
                        allItems = [];
                    }
                }
            } catch (e) {
                console.log('🔄 Not valid JSON, trying markdown parsing...');
                if (objString.includes('character') || objString.includes('Character')) {
                    allItems = parseCharactersFromText(objString);
                } else {
                    allItems = [];
                }
            }
        }
        
        // Ensure we have an array
        if (!Array.isArray(allItems)) {
            console.warn('⚠️ Items is not an array, converting to empty array. Type:', typeof allItems);
            console.warn('⚠️ Actual value:', allItems);
            allItems = [];
        }
        
        // Check if we got any characters specifically
        const currentCharacters = allItems.filter(item => item.category === 'character');
        const currentElements = allItems.filter(item => item.category === 'element');
        
        console.log(`📊 Before fallback: ${currentCharacters.length} characters, ${currentElements.length} elements`);
        
        // FALLBACK: If backend returned no characters but we have a script, parse characters from script
        if (currentCharacters.length === 0 && script) {
            console.log('🔄 No characters from backend, attempting to parse from script...');
            const scriptParsedItems = parseCharactersFromText(script);
            const scriptCharacters = scriptParsedItems.filter(item => item.category === 'character');
            const scriptNarrators = scriptParsedItems.filter(item => item.category === 'narrator');
            
            if (scriptCharacters.length > 0) {
                console.log(`✅ Parsed ${scriptCharacters.length} characters from script!`);
                allItems = [...allItems, ...scriptCharacters];
            }
            if (scriptNarrators.length > 0 && !allItems.some(i => i.category === 'narrator')) {
                console.log(`✅ Parsed ${scriptNarrators.length} narrators from script!`);
                allItems = [...allItems, ...scriptNarrators];
            }
        }
        
        // IMPORTANT: Apply recategorization to ALL items to ensure correct narrator detection
        // This is needed because the backend might return items with incorrect categories
        console.log('🔄 Applying recategorization to all items...');
        allItems = allItems.map(item => {
            const lowerName = (item.name || '').toLowerCase();
            const lowerDescription = (item.description || '').toLowerCase();
            
            // Check for body parts to skip
            const bodyPartPatterns = [
                /\b(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|nose|mouth|lips|teeth|hair|head|face|neck|shoulder|shoulders|chest|back|stomach|knee|knees|elbow|elbows|wrist|wrists|ankle|ankles|thumb|thumbs|toe|toes|skin|nail|nails|palm|palms|forehead|chin|cheek|cheeks|eyebrow|eyebrows|eyelash|eyelashes|beard|mustache|torso|hip|hips|thigh|thighs|calf|calves)\b/i,
                /\b(\w+)'s\s+(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|face|hair|head|body|skin)\b/i
            ];
            
            if (bodyPartPatterns.some(pattern => pattern.test(lowerName))) {
                console.log('⏭️ Skipping body part:', item.name);
                return null;
            }
            
            // Check for narrator - this MUST take priority
            const narratorNamePatterns = [
                /^narrator\b/i,
                /\bnarrator\b/i,
                /^voice\s*over\b/i,
                /^vo\b/i,
                /\bstoryteller\b/i
            ];
            
            const narratorDescPatterns = [
                /\bnarrator\b/i,
                /\bomniscient\b/i,
                /\bvoice\s*over\b/i,
                /\bnarration\b/i,
                /\bstoryteller\b/i,
                /\bnarrates\b/i
            ];
            
            const isNarratorByName = narratorNamePatterns.some(pattern => pattern.test(lowerName));
            const isNarratorByDesc = narratorDescPatterns.some(pattern => pattern.test(lowerDescription));
            
            if (isNarratorByName || isNarratorByDesc) {
                if (item.category !== 'narrator') {
                    console.log(`🎙️ Recategorizing "${item.name}" to narrator (was: ${item.category || 'undefined'})`);
                }
                return { ...item, category: 'narrator' };
            }
            
            // Check for elements
            const elementPatterns = /\b(sink|cloth|lamp|shrine|idol|bell|mist|fog|rain|temple|stone|flame|moss|camera|shot|angle|light|sound|music|building|car|vehicle|tree|forest|city|room|house|office|door|window|table|chair|desk|phone|computer|book|letter|key|mirror|clock|painting|photograph|ring|necklace|sword|gun|knife)\b/i;
            
            if (elementPatterns.test(lowerName)) {
                return { ...item, category: 'element' };
            }
            
            // Default to character if no category or keep existing
            return { ...item, category: item.category || 'character' };
        }).filter(Boolean); // Remove nulls (body parts)
        
        // Filter by category
        const characters = allItems.filter(item => item.category === 'character');
        const otherElements = allItems.filter(item => item.category === 'element');
        const narrators = allItems.filter(item => item.category === 'narrator');
        
        console.log('✅ Final parsed results:', {
            totalItems: allItems.length,
            characters: characters.length,
            otherElements: otherElements.length,
            narrators: narrators.length,
            charactersData: characters,
            otherElementsData: otherElements,
            narratorsData: narrators
        });
        
        // Return characters, other elements, and narrators
        return {
            characters: characters,
            otherElements: otherElements,
            narrators: narrators,
            allItems: allItems,
            credits_used: result?.credits_used || 0
        };
        
    } catch (error) {
        console.error('❌ Character generation failed:', error);
        throw error;
    }
};

/**
 * Generate storyboard from project
 */
export const generateStoryboard = async (project, onProgress = null) => {
    try {
        if (onProgress) onProgress('Submitting storyboard generation...');

        // Feb 13 2026 — Client-side scene detection. Parse the script for
        // explicit scene markers and pass the expected count + titles to
        // the backend as HINTS. The backend LLM has been observed
        // collapsing multi-scene scripts into one; the hints let it know
        // exactly how many scenes to return. We also compare after the
        // fact and warn loudly if the LLM ignored them.
        const parsedScenes = parseScriptScenes(project.script || '');
        const expectedSceneCount = parsedScenes.length;
        const expectedSceneTitles = parsedScenes.map(s => s.title || s.header || `Scene ${s.index}`);
        console.log(`📖 Script parse: expected ${expectedSceneCount} scene(s):`, expectedSceneTitles);

        // Feb 2026 (Option B) — Attach the References-to-video model
        // metadata so the admin backend can log/validate/tune.
        const videoGenPayload = buildVideoGenerationPayload();

        // Feb 2026 — Capability-based model + cascading fallback for the
        // storyboard reasoning LLM. See modelCascadeResolver.js for rules.
        const storyboardCascade = buildCapabilityFallbackChain('storyboard_generation', [
            'storyboard_generation', 'Storyboard Generation', 'generate_storyboard',
        ]);

        const storyboardBody = {
            script: project.script,
            characters: project.characters,
            // Ad creator shot-generation rules — see
            // /app/docs/SHOT_GENERATION_RULES_SPEC.md for the contract.
            // Tells the LLM each shot should be 15 seconds and how to slice
            // the script accordingly. Backend MAY ingest this directly or
            // ignore for backward compatibility — either way always sent.
            shot_generation_rules: buildShotGenerationRules(resolveShotDurationSeconds().seconds),
            // Feb 13 2026 — Explicit scene-count hints. The backend MUST
            // honour these when generating the storyboard scenes[].
            // See SHOT_GENERATION_RULES_SPEC.md § "SCENE preservation".
            expected_scene_count: expectedSceneCount,
            expected_scene_titles: expectedSceneTitles,
        };
        if (videoGenPayload) storyboardBody.video_generation = videoGenPayload;
        if (storyboardCascade.modelKey)                       storyboardBody.model_key       = storyboardCascade.modelKey;
        if (storyboardCascade.fallbackModels?.length)         storyboardBody.fallback_models = storyboardCascade.fallbackModels;

        const result = await apiCall('/ai/generate-storyboard-async', {
            method: 'POST',
            body: JSON.stringify(storyboardBody)
        }, 30000); // 30s — should return job_id quickly

        // ── Async path: poll if we got a job_id ──
        const jobId = result?.job_id || result?.result?.job_id;
        if (jobId) {
            console.log('🔄 Storyboard returned job_id, polling:', jobId);
            if (onProgress) onProgress('Generating storyboard...');

            const completed = await pollForJobResult(jobId, 120, 5000, (progress) => {
                if (onProgress && typeof progress === 'object') {
                    onProgress(`Generating storyboard... ${progress.attempt * 5}s elapsed`);
                }
            });

            const storyboard = completed?.result?.storyboard || completed?.storyboard || completed?.result || {};
            const scenes = storyboard?.scenes || [];
            console.log('✅ Storyboard polled result:', { scenesCount: scenes.length, expectedSceneCount, creditsUsed: completed?.credits_used });
            _warnIfSceneMismatch(scenes.length, expectedSceneCount, expectedSceneTitles);
            return { scenes, credits_used: completed?.credits_used || completed?.credits_cost || 0 };
        }

        // ── Immediate result (no polling needed) ──
        const storyboard = result?.result?.storyboard || result?.result || result?.storyboard || {};
        const scenes = storyboard?.scenes || [];

        console.log('✅ Storyboard generation response:', {
            hasResult: !!result,
            hasStoryboard: !!storyboard,
            scenesCount: scenes?.length || 0,
            expectedSceneCount,
            creditsUsed: result?.credits_used
        });
        _warnIfSceneMismatch(scenes.length, expectedSceneCount, expectedSceneTitles);

        return { scenes, credits_used: result?.credits_used || 0 };

    } catch (error) {
        console.error('❌ Storyboard generation failed:', error);
        throw error;
    }
};

// Feb 13 2026 — Loud diagnostic. When the LLM returns fewer scenes than
// the script has explicit markers for, log a visible warning so the user
// / support can see immediately that the backend LLM ignored the hint.
// Does not throw — a partial storyboard is still salvageable manually.
const _warnIfSceneMismatch = (actualCount, expectedCount, expectedTitles) => {
    if (expectedCount < 2 || actualCount === expectedCount) return;
    if (actualCount < expectedCount) {
        console.error(
            `❌ SCENE MISMATCH: script has ${expectedCount} scenes (${expectedTitles.join(', ')}) but storyboard returned only ${actualCount}. ` +
            `The admin backend LLM likely ignored the "expected_scene_count" hint and/or the shot_generation_rules SCENE preservation clause. ` +
            `Ask the backend team to include ALL rule strings AND expected_scene_count in the LLM system prompt.`
        );
    } else {
        console.warn(
            `⚠️ Storyboard returned ${actualCount} scenes but script had ${expectedCount} explicit markers. ` +
            `Extra scenes may be LLM padding — review and delete if unwanted.`
        );
    }
};

/**
 * Generate frame prompts from shot description
 */
export const generateFramePrompts = async (description, context = {}) => {
    try {
        const {
            characters = [],
            scriptSnippet = '',
            shotIndex = 1,
            sceneIndex = 1,
            totalShots = 1,
            cameraAngle = '',
            vfx = '',
            mood = '',
        } = context;

        // Build a rich director's brief for the backend
        const charBlock = characters.length > 0
            ? `Characters in this shot: ${characters.map(c => `${c.name}${c.description ? ` (${c.description})` : ''}`).join('; ')}.`
            : '';
        const metaBlock = [
            cameraAngle ? `Camera: ${cameraAngle}.` : '',
            vfx && vfx !== 'None' ? `VFX: ${vfx}.` : '',
            mood ? `Mood: ${mood}.` : '',
        ].filter(Boolean).join(' ');

        const directorBrief = [
            `You are a world-class film director and cinematographer.`,
            `You are designing EXACTLY TWO still frames for Shot ${shotIndex} of ${totalShots} in Scene ${sceneIndex}.`,
            ``,
            `IMPORTANT: The shot description below may be in a non-English language. First translate and understand it fully, then generate your output ENTIRELY IN ENGLISH. All prompts you produce MUST be in English — AI image models only understand English prompts.`,
            ``,
            `SHOT DESCRIPTION: ${description}`,
            charBlock,
            metaBlock,
            scriptSnippet ? `\nSTORY CONTEXT (excerpt):\n${scriptSnippet}` : '',
            ``,
            `Think of this shot as a short video clip. Visualise it playing from start to finish.`,
            ``,
            `START FRAME — The very first frame the audience sees when this shot begins.`,
            `Describe the composition, subject placement, lighting, color palette, depth of field, and atmosphere at the opening moment of the shot.`,
            ``,
            `END FRAME — The very last frame before the cut to the next shot.`,
            `Describe how the scene has evolved: how subjects have moved, how lighting or mood has shifted, and the final composition the audience sees.`,
            ``,
            `RULES:`,
            `- OUTPUT LANGUAGE: English only. If the shot description is in any other language, translate it first, then write prompts in English.`,
            `- Each prompt must be a self-contained, photorealistic image description — no references to "start" or "end".`,
            `- Use cinematic language: lens type, lighting direction, color grading, depth of field, composition rules.`,
            `- Describe what the camera SEES, not what happens narratively.`,
            `- Include wardrobe, set design, props, weather, and time-of-day details from the story.`,
            `- Keep each prompt between 60-120 words.`,
            ``,
            `Return ONLY valid JSON: { "startFramePrompt": "...", "endFramePrompt": "..." }`,
            `Both prompts MUST be written in English regardless of the input language.`,
        ].filter(l => l !== null && l !== undefined).join('\n');

        // Feb 2026 — Capability-based model + cascading fallback for the
        // frame-prompt reasoning LLM.
        const framePromptCascade = buildCapabilityFallbackChain('frame_prompts', [
            'frame_prompts', 'Frame Prompts', 'frame_prompt_generation',
        ]);
        const framePromptBody = { description: directorBrief };
        if (framePromptCascade.modelKey)                     framePromptBody.model_key       = framePromptCascade.modelKey;
        if (framePromptCascade.fallbackModels?.length)       framePromptBody.fallback_models = framePromptCascade.fallbackModels;

        // Try backend first
        const result = await apiCall('/ai/generate-frame-prompts', {
            method: 'POST',
            body: JSON.stringify(framePromptBody)
        });

        const start = result?.result?.startFramePrompt;
        const end = result?.result?.endFramePrompt;

        if (start && end) {
            return { startFramePrompt: start, endFramePrompt: end };
        }

        // If backend returned something unexpected, build cinematic fallback
        return buildCinematicFallback(description, characters, cameraAngle, mood);

    } catch (error) {
        console.warn('⚠️ Frame prompt generation failed, using cinematic fallback:', error);
        return buildCinematicFallback(
            description,
            context.characters || [],
            context.cameraAngle || '',
            context.mood || ''
        );
    }
};

function buildCinematicFallback(description, characters = [], cameraAngle = '', mood = '') {
    const subjectNames = characters.map(c => c.name).join(' and ') || 'the subject';
    const cam = cameraAngle || 'medium shot';
    const tone = mood || 'dramatic';

    const startFramePrompt = `Cinematic ${cam}, ${tone} lighting. ${subjectNames} at the beginning of the action — ${description}. Shallow depth of field, film grain, rich color grading, photorealistic detail. Opening composition of the shot.`;
    const endFramePrompt = `Cinematic ${cam}, ${tone} lighting. ${subjectNames} at the culmination of the action — ${description}. The scene has reached its visual climax. Shallow depth of field, film grain, rich color grading, photorealistic detail. Final composition before the cut.`;

    return { startFramePrompt, endFramePrompt };
}

/**
 * Generate video prompt for animation
 * This is generated locally since the backend endpoint may not exist
 */
export const generateVideoPrompt = async (shot, characters, script, duration) => {
    // Generate locally - backend endpoint may not be available
    const shotDesc = shot?.description || 'A cinematic scene';
    const characterNames = characters?.map(c => c.name).join(', ') || '';
    const actionDesc = characterNames ? `${characterNames} in ${shotDesc}` : shotDesc;
    
    return [
        { 
            startTime: "0.0s", 
            endTime: `${duration || 5}s`, 
            action: actionDesc 
        }
    ];
};

// ==================== CREDITS ====================

/**
 * Get current credits balance (quick check)
 */
export const getCreditsBalance = async () => {
    try {
        const result = await apiCall('/credits/balance', {
            method: 'GET'
        });
        
        return {
            credits: result.credits || 0,
            success: true
        };
        
    } catch (error) {
        console.error('❌ Failed to get credits balance:', error);
        return { credits: 0, success: false };
    }
};

/**
 * Get current credits balance (legacy - uses /credits endpoint)
 */
export const getCredits = async () => {
    try {
        const result = await apiCall('/credits', {
            method: 'GET'
        });
        
        return result.credits || 0;
        
    } catch (error) {
        console.error('❌ Failed to get credits:', error);
        // Try the balance endpoint as fallback
        try {
            const balanceResult = await getCreditsBalance();
            return balanceResult.credits;
        } catch (e) {
            return 0;
        }
    }
};

/**
 * Get project credit totals
 * @param {string} projectId - The project ID
 * @param {string[]} jobIds - Array of job IDs from the project
 * @returns {Promise<{total_credits_used: number, available_credits: number}>}
 */
export const getProjectCredits = async (projectId, jobIds = []) => {
    try {
        const result = await apiCall('/credits/project', {
            method: 'POST',
            body: JSON.stringify({
                project_id: projectId,
                job_ids: jobIds
            })
        });
        
        return {
            totalCreditsUsed: result.total_credits_used || 0,
            availableCredits: result.available_credits || 0,
            success: true
        };
        
    } catch (error) {
        console.error('❌ Failed to get project credits:', error);
        return { 
            totalCreditsUsed: 0, 
            availableCredits: 0, 
            success: false 
        };
    }
};

/**
 * Refresh credits display
 */
export const refreshCredits = async (setCreditsCallback) => {
    const credits = await getCredits();
    if (setCreditsCallback) {
        setCreditsCallback(credits);
    }
    return credits;
};

// ==================== AVAILABLE MODELS ====================

// In-memory cache for available models (TTL: 5 min) — avoids refetching during modal opens
let _availableModelsCache = null;
let _availableModelsFetchedAt = 0;
let _availableModelsInFlight = null;
const AVAILABLE_MODELS_TTL_MS = 5 * 60 * 1000;

/**
 * Get available AI models from Admin Backend (with in-memory cache).
 * Returns capabilities with their available models.
 * @param {boolean} forceRefresh - bypass cache and refetch
 */
export const getAvailableModels = async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && _availableModelsCache && (now - _availableModelsFetchedAt) < AVAILABLE_MODELS_TTL_MS) {
        return _availableModelsCache;
    }
    // Dedupe concurrent requests
    if (_availableModelsInFlight) return _availableModelsInFlight;

    _availableModelsInFlight = (async () => {
        try {
            const result = await apiCall('/ai/available-models', { method: 'GET' });
            _availableModelsCache = result.capabilities || [];
            _availableModelsFetchedAt = Date.now();
            return _availableModelsCache;
        } catch (error) {
            console.error('❌ Failed to get available models:', error);
            // Return stale cache if we have one, else empty
            return _availableModelsCache || [];
        } finally {
            _availableModelsInFlight = null;
        }
    })();
    return _availableModelsInFlight;
};

/**
 * Force refresh the available-models cache in the background.
 * Non-blocking — callers don't await this.
 */
export const preloadAvailableModels = () => {
    getAvailableModels(false).catch(() => { /* best-effort preload */ });
};

/**
 * Get models for a specific capability type
 * @param {string} taskType - Task type like 'video_generation', 'image_generation', 'tts', etc.
 */
export const getModelsForTask = async (taskType) => {
    try {
        const capabilities = await getAvailableModels();
        const capability = capabilities.find(c => c.task_type === taskType || c.key === taskType);
        return capability?.models || [];
    } catch (error) {
        console.error(`❌ Failed to get models for ${taskType}:`, error);
        return [];
    }
};

/**
 * Get Image-to-Video models (single image input)
 * Used when shot has only start frame OR only end frame
 */
export const getImageToVideoModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        console.log('🎬 Looking for Image-to-Video models...');
        
        // Keys for single image to video
        const i2vKeys = [
            'Image to video',           // Backend exact key
            'image_to_video',            // Snake case variant
            'i2v',                       // Abbreviation
        ];
        
        for (const key of i2vKeys) {
            const capability = capabilities.find(c => 
                c.key === key || 
                c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            if (capability?.models?.length > 0) {
                console.log('🎬 Found Image-to-Video models under key:', key, capability.models);
                return capability.models;
            }
        }
        
        console.warn('⚠️ No Image-to-Video models found');
        return [];
    } catch (error) {
        console.error('❌ Failed to get Image-to-Video models:', error);
        return [];
    }
};

// Feb 13 2026 — Flatten a capability's models across the top-level
// `models[]` and every `tiers.<tier>` bucket, deduped by model key/id.
// Previously the model-fetch helpers only returned `capability.models[]`
// which meant tier-only models (e.g. Economy's `pixverse-ref2video`) never
// reached the Shot Pre-Production dropdown. The user could pick the model
// in AI Settings but the modal defaulted to the flat catalog's first
// entry (usually the Premium model) — bug report Feb 13 2026.
//
// Exported for direct unit-testing; also used by every getXxxVideoModels
// helper in this file.
export const _flattenCapabilityModels = (capability) => {
    if (!capability) return [];
    const buckets = [];
    if (Array.isArray(capability.models)) buckets.push(capability.models);
    const tiers = capability.tiers || {};
    for (const tierKey of Object.keys(tiers)) {
        if (Array.isArray(tiers[tierKey])) buckets.push(tiers[tierKey]);
    }
    const seen = new Set();
    const out = [];
    for (const bucket of buckets) {
        for (const m of bucket) {
            const idKey = (m?.key || m?.id || m?.name || '').toString().toLowerCase();
            if (!idKey || seen.has(idKey)) continue;
            seen.add(idKey);
            out.push(m);
        }
    }
    return out;
};

/**
 * Get Start-End-Frame Video models (two images input: start + end frames)
 * Used when shot has BOTH start frame AND end frame
 */
export const getStartEndFrameVideoModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        console.log('🎬 Looking for Start-End-Frame video models...');
        
        // Keys for start-end frame video generation
        const startEndKeys = [
            'start-end-frame videos',    // Backend exact key
            'start_end_frame_videos',    // Snake case variant
            'start-end-frame',           // Short variant
            'frame_interpolation',       // Alternative name
            'keyframe_video',            // Alternative name
        ];
        
        for (const key of startEndKeys) {
            const capability = capabilities.find(c => 
                c.key === key || 
                c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            const flattened = _flattenCapabilityModels(capability);
            if (flattened.length > 0) {
                console.log('🎬 Found Start-End-Frame video models under key:', key, `(${flattened.length} incl. tiers)`);
                return flattened;
            }
        }
        
        // Also check for capabilities that contain "start" and "end" or "frame" in their name
        const frameCapability = capabilities.find(c => 
            (c.key && (c.key.toLowerCase().includes('start') || c.key.toLowerCase().includes('frame'))) &&
            !c.key.toLowerCase().includes('image to video')
        );
        const frameFlattened = _flattenCapabilityModels(frameCapability);
        if (frameFlattened.length > 0) {
            console.log('🎬 Found frame video models under capability:', frameCapability.key, `(${frameFlattened.length} incl. tiers)`);
            return frameFlattened;
        }
        
        console.warn('⚠️ No Start-End-Frame video models found');
        return [];
    } catch (error) {
        console.error('❌ Failed to get Start-End-Frame video models:', error);
        return [];
    }
};

/**
 * Get video generation models (Image to Video) - LEGACY function for backward compatibility
 * Tries multiple possible keys as backends may use different naming
 */
export const getVideoModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        console.log('🎬 Looking for video models in capabilities:', capabilities.map(c => ({ key: c.key, task_type: c.task_type })));
        
        // Try multiple possible keys for video generation
        const videoKeys = [
            'Image to video',           // Backend exact key
            'image_to_video',            // Snake case variant
            'video_generation',          // Generic key
            'video',                     // Short key
            'i2v',                       // Abbreviation
        ];
        
        for (const key of videoKeys) {
            const capability = capabilities.find(c => 
                c.key === key || 
                c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            if (capability?.models?.length > 0) {
                console.log('🎬 Found video models under key:', key, capability.models);
                return capability.models;
            }
        }
        
        // Also check for capabilities that contain "video" in their name/key
        const videoCapability = capabilities.find(c => 
            (c.key && c.key.toLowerCase().includes('video')) ||
            (c.task_type && c.task_type.toLowerCase().includes('video'))
        );
        if (videoCapability?.models?.length > 0) {
            console.log('🎬 Found video models under capability:', videoCapability.key, videoCapability.models);
            return videoCapability.models;
        }
        
        console.warn('⚠️ No video models found in capabilities');
        return [];
    } catch (error) {
        console.error('❌ Failed to get video models:', error);
        return [];
    }
};

/**
 * Get all video models (both Image-to-Video and Start-End-Frame) combined
 * Returns { imageToVideo: [], startEndFrame: [] }
 */
/**
 * Get models that support REFERENCES-TO-VIDEO (panel-based shots).
 * The model takes an ORDERED ARRAY of keyframe images and interpolates the
 * video across them. Used by the Shot Pre-Production modal when the shot
 * has a panel structure (project.usesPanelImages = true).
 *
 * Backend capability key candidates (we accept several aliases for
 * forward-compatibility while the admin team decides on the canonical key):
 *   • 'references-to-video'
 *   • 'references_to_video'
 *   • 'reference-to-video'
 *   • 'multi-frame-video'
 *   • 'keyframes-to-video'
 */
export const getReferencesToVideoModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        console.log('🎬 Looking for References-to-Video models...');

        const refKeys = [
            'references-to-video',
            'references_to_video',
            'reference-to-video',
            'reference_to_video',
            'multi-frame-video',
            'multi_frame_video',
            'keyframes-to-video',
            'keyframes_to_video',
        ];

        for (const key of refKeys) {
            const capability = capabilities.find(c =>
                c.key === key ||
                c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            const flattened = _flattenCapabilityModels(capability);
            if (flattened.length > 0) {
                console.log('🎬 Found References-to-Video models under key:', key, `(${flattened.length} incl. tiers)`);
                return flattened;
            }
        }

        // Also check for any capability containing "reference" + "video"
        const refCapability = capabilities.find(c =>
            (c.key && c.key.toLowerCase().includes('reference') && c.key.toLowerCase().includes('video'))
        );
        const refFlattened = _flattenCapabilityModels(refCapability);
        if (refFlattened.length > 0) {
            console.log('🎬 Found References-to-Video under capability:', refCapability.key, `(${refFlattened.length} incl. tiers)`);
            return refFlattened;
        }

        console.warn('⚠️ No References-to-Video models found in admin catalog');
        return [];
    } catch (error) {
        console.error('❌ Failed to get References-to-Video models:', error);
        return [];
    }
};

export const getAllVideoModels = async () => {
    const [imageToVideoModels, startEndFrameModels, referencesToVideoModels] = await Promise.all([
        getImageToVideoModels(),
        getStartEndFrameVideoModels(),
        getReferencesToVideoModels()
    ]);

    return {
        imageToVideo:       imageToVideoModels,
        startEndFrame:      startEndFrameModels,
        referencesToVideo:  referencesToVideoModels
    };
};

/**
 * Get image generation models
 */
export const getImageModels = async () => {
    return getModelsForTask('image_generation');
};

/**
 * Get TTS (text-to-speech) models
 */
export const getTTSModels = async () => {
    return getModelsForTask('tts');
};

/**
 * Get voice cloning models
 */
export const getVoiceCloningModels = async () => {
    return getModelsForTask('voice_cloning');
};

// ==================== LIPSYNC ====================

/**
 * Generate lipsync video
 */
export const generateLipsync = async (videoUrl, audioUrl, onProgress) => {
    try {
        if (onProgress) onProgress('Generating lipsync...');
        
        const result = await apiCall('/ai/generate-lipsync', {
            method: 'POST',
            body: JSON.stringify({
                video_url: videoUrl,
                audio_url: audioUrl
            })
        });
        
        return {
            success: true,
            videoUrl: result.result?.video_url,
            jobId: result.job_id,
            creditsUsed: result.credits_used
        };
        
    } catch (error) {
        console.error('❌ Lipsync generation failed:', error);
        throw error;
    }
};

// ==================== VOICE CHANGER ====================

/**
 * Change the voice in a video using ElevenLabs voice cloning
 * POST /api/ai/change-voice-async → poll /api/ai/jobs/{job_id}
 */
export const changeVideoVoice = async (videoUrl, voiceId, onProgress) => {
    try {
        if (onProgress) onProgress('Submitting voice change request...');
        
        console.log('🎙️ Voice change request:', { videoUrl: videoUrl?.substring(0, 80), voiceId });
        
        const result = await apiCall('/ai/change-voice-async', {
            method: 'POST',
            body: JSON.stringify({
                video_url: videoUrl,
                voice_id: voiceId
            })
        }, 30000);
        
        const jobId = result.job_id || result.result?.job_id;
        
        if (jobId) {
            console.log('🔄 Voice change job submitted, polling:', jobId);
            if (onProgress) onProgress('Changing voice...');
            
            const completed = await pollForJobResult(jobId, 60, 5000, (progress) => {
                if (onProgress && typeof progress === 'object') {
                    onProgress(`Changing voice... ${progress.attempt * 5}s elapsed`);
                }
            });
            
            const videoResultUrl = completed.video_url || completed.result?.video_url || completed.result?.result?.video_url;
            console.log('✅ Voice changed:', videoResultUrl?.substring(0, 80));
            return {
                success: !!videoResultUrl,
                videoUrl: videoResultUrl,
                jobId,
                creditsUsed: completed.credits_used || completed.credits_cost || 0
            };
        }
        
        // Immediate result
        const videoResultUrl = result.result?.video_url || result.video_url;
        return {
            success: !!videoResultUrl,
            videoUrl: videoResultUrl,
            creditsUsed: result.credits_used || 0
        };
        
    } catch (error) {
        console.error('❌ Voice change failed:', error);
        throw error;
    }
};


/**
 * Change voice for a specific time segment of a video.
 * Sends start_time and end_time so the backend only changes voice for that portion.
 */
export const changeVideoVoiceSegment = async (videoUrl, voiceId, startTime, endTime, onProgress) => {
    try {
        if (onProgress) onProgress(`Submitting voice change for segment ${startTime.toFixed(1)}s-${endTime.toFixed(1)}s...`);
        
        console.log('🎙️ Segment voice change request:', { videoUrl: videoUrl?.substring(0, 80), voiceId, startTime, endTime });
        
        const result = await apiCall('/ai/change-voice-async', {
            method: 'POST',
            body: JSON.stringify({
                video_url: videoUrl,
                voice_id: voiceId,
                start_time: startTime,
                end_time: endTime
            })
        }, 30000);
        
        const jobId = result.job_id || result.result?.job_id;
        
        if (jobId) {
            console.log('🔄 Segment voice change job submitted, polling:', jobId);
            if (onProgress) onProgress('Changing voice for segment...');
            
            const completed = await pollForJobResult(jobId, 60, 5000, (progress) => {
                if (onProgress && typeof progress === 'object') {
                    onProgress(`Changing voice... ${progress.attempt * 5}s elapsed`);
                }
            });
            
            const videoResultUrl = completed.video_url || completed.result?.video_url || completed.result?.result?.video_url;
            console.log('✅ Segment voice changed:', videoResultUrl?.substring(0, 80));
            return {
                success: !!videoResultUrl,
                videoUrl: videoResultUrl,
                jobId,
                creditsUsed: completed.credits_used || completed.credits_cost || 0
            };
        }
        
        const videoResultUrl = result.result?.video_url || result.video_url;
        return {
            success: !!videoResultUrl,
            videoUrl: videoResultUrl,
            creditsUsed: result.credits_used || 0
        };
        
    } catch (error) {
        console.error('❌ Segment voice change failed:', error);
        throw error;
    }
};


/**
 * Re-poll a previously submitted job to fetch its result.
 * Used for batch operations where initial polling timed out but the job may have completed.
 */
export const rePollJob = async (jobId, maxAttempts = 20, intervalMs = 5000) => {
    console.log(`🔄 Re-polling job ${jobId}...`);
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const result = await apiCall(`/ai/jobs/${jobId}`, { method: 'GET' });
            if (result.status === 'completed' || result.status === 'success') {
                const imageUrl = result.result?.image_url || result.result?.images?.[0] || result.image_url || result.images?.[0];
                return { success: true, imageUrl, result };
            }
            if (result.status === 'failed' || result.status === 'error') {
                return { success: false, error: result.error || 'Job failed' };
            }
            await new Promise(r => setTimeout(r, intervalMs));
        } catch (err) {
            if (attempt === maxAttempts) return { success: false, error: err.message };
            await new Promise(r => setTimeout(r, intervalMs));
        }
    }
    return { success: false, error: 'Re-poll timed out' };
};


// ==================== TRANSCRIPTION ====================

/**
 * Transcribe audio to text
 * Supports both JSON and FormData approaches for different backend implementations
 */
export const transcribeAudio = async (audioData) => {
    try {
        // Handle different audio data formats
        let audioBase64 = audioData;
        let mimeType = 'audio/webm';
        
        if (typeof audioData === 'string' && audioData.startsWith('data:')) {
            const matches = audioData.match(/^data:([^;]+);base64,(.+)$/);
            if (matches) {
                mimeType = matches[1];
                audioBase64 = matches[2];
            } else {
                audioBase64 = audioData.split(',')[1] || audioData;
            }
        }
        
        // Get the selected model for transcription from AI toolkit
        const selectedModel = getModelForCapability('transcribe-audio2txt') || 
                              getModelForCapability('Audio Transcription');
        
        console.log('🎤 Transcription request:', {
            audioLength: audioBase64?.length || 0,
            mimeType,
            selectedModel: selectedModel || '(default)',
            capability: 'transcribe-audio2txt'
        });

        // Convert base64 to blob
        const byteCharacters = atob(audioBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const fileExtension = mimeType.split('/')[1] || 'webm';
        const audioBlob = new Blob([byteArray], { type: mimeType });
        
        // Auth headers
        const apiKey = sessionStorage.getItem('apiKey');
        const userEmail = sessionStorage.getItem('userEmail');
        const headers = {};
        if (apiKey) headers['X-API-Key'] = apiKey;
        if (userEmail) headers['X-User-Email'] = userEmail;

        // Build minimal FormData — just the audio file and model_key
        const formData = new FormData();
        formData.append('file', audioBlob, `recording.${fileExtension}`);
        if (selectedModel) formData.append('model_key', selectedModel);

        // Try the capability-based endpoint first: /api/ai/transcribe-audio2txt
        const endpoints = ['/ai/transcribe-audio2txt', '/ai/transcribe'];
        
        for (const endpoint of endpoints) {
            try {
                console.log(`🎤 Trying ${endpoint}...`);
                const response = await fetch(`${getUserBackendUrl()}${endpoint}`, {
                    method: 'POST',
                    headers,
                    body: formData
                });
                
                if (response.ok) {
                    const result = await response.json();
                    console.log(`✅ Transcription succeeded via ${endpoint}:`, result);
                    return extractTranscriptionText(result);
                }
                
                const errorText = await response.text().catch(() => `Status ${response.status}`);
                console.warn(`⚠️ ${endpoint} returned ${response.status}:`, errorText.substring(0, 200));
                
                // If it's a 404, try next endpoint. For other errors, still try next.
                continue;
            } catch (err) {
                console.warn(`⚠️ ${endpoint} failed:`, err.message);
                continue;
            }
        }
        
        throw new Error('Transcription failed on all endpoints. Please check AI toolkit configuration.');
        
    } catch (error) {
        console.error('❌ Transcription failed:', error);
        throw error;
    }
};

function extractTranscriptionText(result) {
    return result.result?.text || 
           result.result?.transcript ||
           result.result?.transcription ||
           result.text ||
           result.transcript ||
           result.transcription ||
           (typeof result.result === 'string' ? result.result : '') ||
           '';
}


// ==================== ADVANCED VIDEO EDITING ====================

/**
 * Advanced video edit using the "Video editing" capability from AI toolkit.
 * Sends the video URL, reference images, and a composite prompt.
 * Image URLs are passed as `reference_images` — same pattern used for frame/image generation.
 *
 * @param {string} videoUrl - URL of the video to edit
 * @param {string[]} referenceImages - Array of reference image URLs (from all elements)
 * @param {string} prompt - Composite prompt with element-specific instructions
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<{success: boolean, videoUrl: string, jobId: string, creditsUsed: number}>}
 */
export const editVideoAdvanced = async (videoUrl, referenceImages, prompt, onProgress) => {
    try {
        if (onProgress) onProgress('Submitting advanced video edit...');

        // Resolve the model from the "Video editing" capability
        const modelKey = getModelForCapability('video_editing')
                      || getModelForCapability('Video editing')
                      || 'default';

        console.log('🎬 Advanced video edit — model_key:', modelKey, 'ref_images:', Array.isArray(referenceImages) ? referenceImages.length : 0, 'prompt:', prompt);

        const body = {
            video_url: videoUrl,
            model_key: modelKey,
            prompt: prompt || '',
        };
        // Nov 2026 — Reference images are optional. Omit the field entirely
        // when the user hasn't provided any so the admin backend treats this
        // as a pure text-driven edit instead of a "0-refs" request some
        // models reject.
        if (Array.isArray(referenceImages) && referenceImages.length > 0) {
            body.reference_images = referenceImages;
        }

        const result = await apiCall('/ai/edit-video', {
            method: 'POST',
            body: JSON.stringify(body)
        }, 300000); // 5-min timeout — editing can be slow

        // Async path: poll for result
        const jobId = result.job_id || result.result?.job_id;
        if (jobId) {
            if (onProgress) onProgress('Video edit in progress...');
            const completed = await pollForVideoJobResult(jobId, 120, 5000, onProgress);
            const resultUrl = completed.video_url || completed.videoUrl;
            if (!resultUrl) throw new Error('Job completed but no video URL returned.');
            return {
                success: true,
                videoUrl: resultUrl,
                jobId,
                creditsUsed: completed.credits_used || 0,
                creditsRemaining: completed.credits_remaining
            };
        }

        // Sync path: video URL returned directly
        const directUrl = result.video_url
                       || result.result?.video_url
                       || result.result?.result?.video_url
                       || parseVideoUrl(result);
        if (directUrl) {
            return {
                success: true,
                videoUrl: directUrl,
                creditsUsed: result.credits_used || 0,
                creditsRemaining: result.credits_remaining
            };
        }

        throw new Error('No job_id or video_url returned from video edit endpoint.');
    } catch (error) {
        console.error('❌ Advanced video edit failed:', error);
        throw error;
    }
};

// ==================== VIDEO UPSCALE ====================

export const upscaleVideo = async (videoUrl, onProgress) => {
    try {
        if (onProgress) onProgress('Submitting upscale request...');

        const result = await apiCall('/ai/upscale-video', {
            method: 'POST',
            body: JSON.stringify({ video_url: videoUrl })
        }, 120000);

        const jobId = result.job_id || result.result?.job_id;
        const immediateUrl = result.result?.video_url || result.video_url;

        if (jobId && !immediateUrl) {
            if (onProgress) onProgress('Upscaling in progress...');
            const completed = await pollForVideoJobResult(jobId, 60, 10000, onProgress);
            return { success: true, videoUrl: parseVideoUrl(completed), jobId, creditsUsed: result.credits_used };
        }
        return { success: true, videoUrl: immediateUrl, jobId, creditsUsed: result.credits_used };
    } catch (error) {
        console.error('Upscale video failed:', error);
        throw error;
    }
};

// ==================== VIDEO RELIGHT ====================

export const relightVideo = async (videoUrl, prompt, onProgress) => {
    try {
        if (onProgress) onProgress('Submitting relight request...');

        const result = await apiCall('/ai/relight-video', {
            method: 'POST',
            body: JSON.stringify({ video_url: videoUrl, prompt })
        }, 120000);

        const jobId = result.job_id || result.result?.job_id;
        const immediateUrl = result.result?.video_url || result.video_url;

        if (jobId && !immediateUrl) {
            if (onProgress) onProgress('Relighting in progress...');
            const completed = await pollForVideoJobResult(jobId, 60, 10000, onProgress);
            return { success: true, videoUrl: parseVideoUrl(completed), jobId, creditsUsed: result.credits_used };
        }
        return { success: true, videoUrl: immediateUrl, jobId, creditsUsed: result.credits_used };
    } catch (error) {
        console.error('Relight video failed:', error);
        throw error;
    }
};

// ==================== MOTION CONTROL ====================

/**
 * Get models for the Motion Control capability from AI toolkit
 */
export const getMotionControlModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        const motionKeys = [
            'Motion control', 'motion_control', 'Motion Control',
            'motion control', 'motion-control',
        ];
        for (const key of motionKeys) {
            const cap = capabilities.find(c =>
                c.key === key || c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            if (cap?.models?.length > 0) {
                console.log('🎮 Found Motion Control models:', cap.models);
                return cap.models;
            }
        }
        // Fallback: also check image_to_video models that support motion
        const i2vModels = await getImageToVideoModels();
        return i2vModels;
    } catch (error) {
        console.error('Failed to get Motion Control models:', error);
        return [];
    }
};

export const generateMotionControlVideo = async (imageUrl, motionVideoUrl, prompt, aspectRatio, onProgress, modelKey, duration, resolution) => {
    try {
        if (onProgress) onProgress('Submitting motion control request...');

        const resolvedModelKey = modelKey || getModelForCapability('motion_control') || getModelForCapability('image_to_video');

        const body = {
            prompt: prompt || 'Apply the motion from the reference video to the image',
            image_url: imageUrl,
            model_key: resolvedModelKey,
            aspect_ratio: aspectRatio
        };
        if (motionVideoUrl) body.reference_video_url = motionVideoUrl;
        if (duration) body.duration = duration;
        if (resolution) body.resolution = resolution;

        if (onProgress) onProgress('Generating motion video (may take 1-3 minutes)...');

        // POST to start the job — 300s timeout handles both:
        // - Current sync backend (waits 60-180s for fal.ai)
        // - Future async backend (returns job_id in ~2s)
        const result = await apiCall('/ai/generate-motion-control', {
            method: 'POST',
            body: JSON.stringify(body)
        }, 300000);

        const jobId = result.job_id || result.result?.job_id;

        if (jobId) {
            // Async backend — poll for result
            if (onProgress) onProgress('Motion control video is being generated...');
            const completed = await pollForVideoJobResult(jobId, 120, 5000, onProgress);
            const videoUrl = completed.video_url || completed.videoUrl;
            if (!videoUrl) throw new Error('Job completed but no video URL found.');
            return { success: true, videoUrl, jobId, creditsUsed: completed.credits_used || 0, creditsRemaining: completed.credits_remaining };
        }

        // Sync backend — video URL returned directly
        const videoUrl = result.video_url || result.result?.video_url || result.result?.result?.video?.url || parseVideoUrl(result);
        if (videoUrl) {
            return { success: true, videoUrl, creditsUsed: result.credits_used || 0, creditsRemaining: result.credits_remaining };
        }

        throw new Error('No job_id or video_url returned from motion control endpoint.');
    } catch (error) {
        console.error('Motion control video failed:', error);
        throw error;
    }
};

// Export configuration helper
export const getConfig = () => ({
    userBackendUrl: getUserBackendUrl()
});
